
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase/client';
import { ladeAnalysenFuerEmails, setzeCockpitStatus, setzeWerbungManuell, type EmailAnalyse } from '../../../lib/ki/analyseApi';
import { ladeRechnungStatusFuerEmails, type EmailRechnungBadge } from '../../../lib/rechnungen/emailRechnungStatus';
import { ladeAblageStatusFuerEmails } from '../../../lib/dokumente/emailAblageStatus';
import { useEmailNav, type EmailOrdner } from './EmailNavContext';
import { MAIL_FENSTER_MERKMALE, MAIL_FENSTER_NAME, alsMailFensterNachricht, mailFensterPfad } from '../../../lib/emails/mailFenster';
import { ordnerLabel, ziehAenderung } from '../../../components/immobilien/sidebar/emailOrdner';
import { useIslandActions } from '../../../components/island/IslandProvider';
import { verknuepfeTicketMitEmail } from '../../../lib/ki/ticketLink';
import { istTriagierbar } from '../../../lib/ki/triage';
import { empfaengerAlsText } from '../../../lib/emails/empfaenger';
import { mailTrifft } from '../../../lib/emails/mailSuche';
import { rueckmeldungEtikett } from '../../../lib/emails/rueckmeldung';
import { antwortZeit, antwortZu, letzteAntwortJeVerlauf, type LetzteAntwort } from '../../../lib/emails/beantwortet';
import { alleSeiten } from '../../../lib/supabase/alleSeiten';
import { replyKontextAusEmail, forwardKontextAusEmail, type ReplyKontext, type ForwardKontext } from '../../../lib/emails/verfassenKontext';
import { istWiedervorlageFaellig } from '../../../lib/emails/wiedervorlage';
import { ablaufGrenze, aktiveBearbeitung, effektiveZustaendigkeit, kurzname, seitText, zustaendigFarben } from '../../../lib/emails/bearbeitung';
import { matchtZustaendigkeitsFilter, resolveZustaendig, type ZustaendigTyp } from '../../../lib/rechnungen/zustaendigkeit';
import EmailDetailsSidebar from './EmailDetailsSidebar';
import EmailVerfassen from './EmailVerfassen';
import KontaktErstellenFormular from '../kontakte/KontaktErstellenFormular';
import EmailKontoEinstellungen from './EmailKontoEinstellungen';
import EmailKontoFormular from './EmailKontoFormular';
import EmailVorlagenGrid from './EmailVorlagenGrid';
import TicketFormular from '../tickets/TicketFormular';
import TerminFormular from '../kalender/TerminFormular';
import { terminAusMail, type TerminAusMail } from '../../../lib/emails/terminAusMail';
import { mailKlartext } from '../../../lib/emails/klartext';
import {
  AddressBook as PhAddressBook, Archive as PhArchive, CalendarPlus as PhCalendarPlus, CheckCircle as PhCheckCircle, CircleNotch, Clock as PhClock, EnvelopeOpen, EnvelopeSimple, Megaphone as PhMegaphone,
  PaperPlaneTilt, PencilSimpleLine as PhInBearbeitung, Hourglass as PhHourglass, Star as PhStar, Tray, UserPlus as PhUserPlus, WarningCircle, X as PhX,
} from '@phosphor-icons/react';
import { BestaetigenDialog, GlassModal, LeerZustand, SchwebenderToast, useToastPill } from '../../../components/immobilien/ui';
import {
  datumsGruppe, gruppiereNachKonversation, initialen, kuerze, relativesDatum, type ZeilenBadge, type ZuordnungsChip,
} from '../../../components/immobilien/email/listenAnsicht';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { ListenKopf } from './bereich/liste/ListenKopf';
import { EmailZeile, type KiKennzeichen } from './bereich/liste/EmailZeile';
import { Sammelleiste } from './bereich/liste/Sammelleiste';
import { EmailKontextmenue, WiedervorlageWahl, ZuweisenWahl, type MenueAktion } from './bereich/liste/EmailKontextmenue';
import type { FilterPanelProps } from './bereich/liste/FilterPanel';

// ─── Types ───────────────────────────────────────────────────────────────────

interface EmailListeProps {
  userProfile?: any;
  initialEmailId?: string | null;
  onClearInitial?: () => void;
  /** Neues Design (EmailBereich): Kopf kommt von außen, Ordner/Objekt stehen in der URL. */
  neu?: boolean;
  /** Inhalt des Ordners „Serienmail“ (Läufe, Assistent, Detail). */
  serienmail?: React.ReactNode;
  /** #551: eigenes Browserfenster für EINE Mail (Route /mail-fenster/:id) — Lesefenster im Vollbild, Schließen schließt das Fenster. */
  fenster?: boolean;
}

interface EmailKonto {
  id: string;
  email_adresse: string;
  anzeigename: string | null;
  aktiv: boolean;
  bezeichnung?: string | null;
  letzter_sync?: string | null;
}

interface Email {
  id: string;
  konto_id: string;
  richtung: 'eingang' | 'ausgang';
  von: string;
  /** Anzeigename des Absenders aus dem Graph-Sync — NULL bei Altbestand. */
  von_name?: string | null;
  /** #544: im Ordner „Gelöscht“ */
  geloescht_am?: string | null;
  /** #551: wartet auf Rückmeldung seit */
  rueckmeldung_seit?: string | null;
  // emails.an ist text[] (Graph-Sync); Helper toEmpfaengerListe bleibt als dünne Normalisierung.
  an: string[] | null;
  /** Nötig für „Allen antworten" UND für die Cc-Zeile im Detail — ohne cc im Select blieb sie leer. */
  cc?: string[] | null;
  betreff: string;
  text_inhalt: string | null;
  html_inhalt?: string | null; // in der Liste NICHT geladen (Performance) — im Detail nachgeladen
  message_id?: string | null;
  conversation_id?: string | null;
  datum: string;
  gelesen: boolean;
  markiert: boolean;
  erledigt?: boolean;
  /** #573: wann/wer/welche Automatik erledigt hat */
  erledigt_am?: string | null;
  erledigt_von?: string | null;
  erledigt_automatisch?: string | null;
  zustaendig_id?: string | null;
  zustaendig_typ?: string | null;
  /** #527: wer die Mail gerade bearbeitet */
  in_bearbeitung_von?: string | null;
  in_bearbeitung_seit?: string | null;
  hat_anhaenge?: boolean;
  ist_werbung?: boolean;
  archiviert_am?: string | null;
  wiedervorlage_am?: string | null;
  geplant_am?: string | null;
  ordner?: string | null;
  mieter_id: string | null;
  eigentuemer_id: string | null;
  objekt_id: string | null;
  created_at: string;
  // Joined
  mieter?: { vorname: string; nachname: string; firma_name?: string | null; einheit?: string | null; email?: string | null } | null;
  eigentuemer?: { vorname: string; nachname: string; firma_name?: string | null; email?: string | null } | null;
  objekt?: { bezeichnung: string; zustaendig?: string | null; zustaendig_typ?: string | null; zustaendig_id?: string | null } | null;
}

// Gematchter Kontakt-Name (Mieter/Eigentümer) statt Roh-E-Mail — ABER nur, wenn dieser Kontakt die
// Mail auch wirklich gesendet hat (seine E-Mail == Absender). Sonst würde bei inhaltlich zugeordneten
// Mails (z. B. ein Newsletter, der einen Eigentümernamen erwähnt) fälschlich der zugeordnete Kontakt
// als Absender erscheinen — obwohl er gar nicht der Absender ist.
const kontaktName = (email: Email): string | null => {
  const von = (email.von || '').trim().toLowerCase();
  if (!von) return null;
  if (email.mieter && (email.mieter.email || '').trim().toLowerCase() === von)
    return email.mieter.firma_name || `${email.mieter.vorname} ${email.mieter.nachname}`.trim();
  if (email.eigentuemer && (email.eigentuemer.email || '').trim().toLowerCase() === von)
    return email.eigentuemer.firma_name || `${email.eigentuemer.vorname} ${email.eigentuemer.nachname}`.trim();
  return null;
};
type TabKey = 'posteingang' | 'offen' | 'erledigt' | 'gesendet' | 'geplant' | 'entwuerfe' | 'wiedervorlage' | 'rueckmeldung' | 'werbung' | 'archiviert' | 'geloescht' | 'vorlagen' | 'serienmail' | 'einstellungen' | 'objekt';
// Spalten der Mailliste (ohne html_inhalt — das lädt erst die Detailansicht).
const LISTEN_SPALTEN = 'id, konto_id, richtung, von, von_name, an, cc, betreff, text_inhalt, message_id, conversation_id, datum, gelesen, markiert, erledigt, erledigt_am, erledigt_von, erledigt_automatisch, hat_anhaenge, ist_werbung, archiviert_am, geloescht_am, rueckmeldung_seit, wiedervorlage_am, geplant_am, ordner, mieter_id, eigentuemer_id, objekt_id, zustaendig_id, zustaendig_typ, in_bearbeitung_von, in_bearbeitung_seit, created_at, mieter:mieter_id(vorname, nachname, firma_name, einheit, email), eigentuemer:eigentuemer_id(anrede, vorname, nachname, firma_name, email), objekt:objekt_id(bezeichnung, zustaendig, zustaendig_typ, zustaendig_id)';
const TAB_KEYS: readonly TabKey[] = ['posteingang', 'offen', 'erledigt', 'gesendet', 'geplant', 'entwuerfe', 'wiedervorlage', 'rueckmeldung', 'werbung', 'archiviert', 'geloescht', 'vorlagen', 'serienmail', 'einstellungen', 'objekt'];
const alsTab = (v: string | null): TabKey | null => (v && (TAB_KEYS as readonly string[]).includes(v) ? (v as TabKey) : null);

// ─── Helpers ─────────────────────────────────────────────────────────────────

// ─── Component ───────────────────────────────────────────────────────────────

const EmailListe: React.FC<EmailListeProps> = ({ userProfile, initialEmailId, onClearInitial, neu = false, serienmail, fenster = false }) => {
  // Neues Design: Ordner und Objekt stehen in der URL (?ordner=…&objekt=…), Filter weiter in localStorage.
  const [urlParams, setUrlParams] = useSearchParams();
  const urlOrdner = neu ? alsTab(urlParams.get('ordner')) : null;
  const urlObjekt = neu ? urlParams.get('objekt') : null;
  // State: Accounts
  const [konten, setKonten] = useState<EmailKonto[]>([]);
  const [selectedKontoId, setSelectedKontoId] = useState<string>('');
  const [kontenLoading, setKontenLoading] = useState(true);

  // State: Emails
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(false);
  // Explizites "echter Fetch abgeschlossen"-Signal (siehe initialEmailId-Effekt unten) — anders
  // als `loading` (startet false, bevor überhaupt ein Fetch lief) ist dies erst true, NACHDEM
  // fetchEmails() für das aktuell gewählte Konto wirklich durchgelaufen ist.
  const [fetchAbgeschlossen, setFetchAbgeschlossen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [objekte, setObjekte] = useState<{ id: string; objektnummer: string | null; bezeichnung: string | null }[]>([]);
  const [selectedEmailIds, setSelectedEmailIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Konversations-Ansicht (E5): Mails mit gleicher conversation_id gruppiert,
  // Wahl bleibt pro Browser erhalten.
  const [gruppiereKonversationen, setGruppiereKonversationen] = useState<boolean>(() => {
    try { return localStorage.getItem('immo_email_konversationen') !== 'aus'; } catch { return true; }
  });
  const toggleKonversationen = () => setGruppiereKonversationen((v) => {
    try { localStorage.setItem('immo_email_konversationen', v ? 'aus' : 'an'); } catch { /* egal */ }
    return !v;
  });
  const [expandedConvs, setExpandedConvs] = useState<Set<string>>(new Set());
  const [objektUnread, setObjektUnread] = useState<Record<string, number>>({});
  const [selectedObjektId, setSelectedObjektId] = useState<string | null>(urlOrdner === 'objekt' ? urlObjekt : null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  // State: UI
  const savedState = (() => {
    try {
      const saved = localStorage.getItem('email_filters');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  })();

  const [activeTab, setActiveTab] = useState<TabKey>(urlOrdner ?? alsTab(savedState?.tab ?? null) ?? 'posteingang');

  // URL → Zustand (Zurück-Taste, Weiterleitung von /admin/serienmail) und Zustand → URL
  useEffect(() => {
    if (!neu) return;
    if (urlOrdner && urlOrdner !== activeTab) setActiveTab(urlOrdner);
    if (urlOrdner === 'objekt' && urlObjekt && urlObjekt !== selectedObjektId) setSelectedObjektId(urlObjekt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neu, urlOrdner, urlObjekt]);
  useEffect(() => {
    if (!neu) return;
    const objekt = activeTab === 'objekt' ? selectedObjektId : null;
    if (urlOrdner === activeTab && (urlObjekt ?? null) === (objekt ?? null)) return;
    setUrlParams(p => {
      const n = new URLSearchParams(p);
      n.set('ordner', activeTab);
      if (objekt) n.set('objekt', objekt); else n.delete('objekt');
      // Serienmail-Lauf gehört nur zum Ordner Serienmail
      if (activeTab !== 'serienmail') n.delete('lauf');
      return n;
    }, { replace: !urlOrdner });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neu, activeTab, selectedObjektId]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterGelesen, setFilterGelesen] = useState<'alle' | 'gelesen' | 'ungelesen'>(savedState?.gelesen || 'alle');
  const [filterZuordnung, setFilterZuordnung] = useState<'alle' | 'mit' | 'ohne'>(savedState?.zuordnung || 'alle');
  const [filterTriage, setFilterTriage] = useState<'alle' | 'ohne_ticket' | 'notfall'>(savedState?.triage || 'alle');
  const [filterErledigt, setFilterErledigt] = useState<'alle' | 'offen' | 'erledigt'>(savedState?.erledigt || 'alle');
  const [filterZustaendig, setFilterZustaendig] = useState<string>(savedState?.zustaendig || 'alle');
  const [filterMarkiert, setFilterMarkiert] = useState<boolean>(savedState?.markiert || false);
  const [filterAnhang, setFilterAnhang] = useState<boolean>(savedState?.anhang || false);
  const [teams, setTeams] = useState<{ id: string; name: string }[]>([]);
  const [teamMembers, setTeamMembers] = useState<{ id: string; full_name: string }[]>([]);
  // #527: feste Farbe je Team/Mitarbeiter für die Liste
  const farben = useMemo(() => zustaendigFarben(teams, teamMembers), [teams, teamMembers]);
  const meineId: string | null = typeof userProfile?.id === 'string' ? userProfile.id : null;
  const { mobil, quer } = useMobilModus();
  const [lesenVollbild, setLesenVollbild] = useState(fenster);
  // #551: Postfach und Mailfenster sprechen über einen Browser-Kanal — Änderungen im Mailfenster
  // (gelesen, erledigt, gelöscht …) lassen das Postfach still nachladen, ohne Sprung.
  const kanalRef = useRef<BroadcastChannel | null>(null);
  const nachladenRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const k = new BroadcastChannel('immo-emails');
    kanalRef.current = k;
    if (!fenster) k.onmessage = (e) => {
      // #572: Bittet das Mailfenster ums Schließen (eigenes window.close() griff nicht), schließt es das Postfach.
      if (alsMailFensterNachricht(e.data)?.typ === 'schliessen') { mailFensterRef.current?.close(); return; }
      nachladenRef.current();
    };
    return () => { k.close(); kanalRef.current = null; };
  }, [fenster]);
  // #572: das EINE Mailfenster, das dieses Postfach geöffnet hat
  const mailFensterRef = useRef<Window | null>(null);
  const meldeAenderung = () => { if (fenster) kanalRef.current?.postMessage('geaendert'); };
  // Fehlermeldungen als Toast statt alert(), gleiche Texte (Entscheidung 8).
  const [toast, zeigeToast] = useToastPill();
  // Rückfrage vor endgültigem Löschen – Sammel (ids) oder einzeln aus dem Kontextmenü (Entscheidung 7).
  const [loeschFrage, setLoeschFrage] = useState<{ ids: string[] } | { email: Email } | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [emailMenu, setEmailMenu] = useState<{ x: number; y: number; email: Email } | null>(null);
  // Picker-Trigger fürs Wiedervorlage-Datum — der Picker selbst kommt in Task 3.
  const [wiedervorlagePicker, setWiedervorlagePicker] = useState<{ emailId: string; x: number; y: number } | null>(null);
  // #527: Zuweisen per Rechtsklick
  const [zuweisenPicker, setZuweisenPicker] = useState<{ email: Email; x: number; y: number } | null>(null);
  // Schreibfenster (E4): Antworten/Weiterleiten, Neue E-Mail und Entwürfe öffnen alle dasselbe Fenster.
  // `nachher` läuft nach Schließen/Senden (z. B. Entwürfe-Zähler, sobald das Auto-Speichern durch ist).
  const [verfassen, setVerfassen] = useState<{
    replyTo?: ReplyKontext; forward?: ForwardKontext; draft?: React.ComponentProps<typeof EmailVerfassen>['draft'];
    titel?: string; nachher?: () => void; nr: number;
    /** #553: Eingangsmail, auf die geantwortet wird — nach dem Senden „Als erledigt markieren?“ */
    antwortAuf?: Email;
  } | null>(null);
  const verfassenNr = useRef(0);
  const oeffneVerfassen = (v: Omit<NonNullable<typeof verfassen>, 'nr'>) => { verfassenNr.current += 1; setVerfassen({ ...v, nr: verfassenNr.current }); };
  const [kontoZumBearbeiten, setKontoZumBearbeiten] = useState<any>(null);
  const [ticketInitialData, setTicketInitialData] = useState<{ titel: string; beschreibung: string; objekt_id?: string; mieter_id?: string; kategorie_id?: string; prioritaet?: string } | null>(null);
  const [ticketRefresh, setTicketRefresh] = useState(0);
  const [ticketKategorien, setTicketKategorien] = useState<any[]>([]);
  const [ticketTeamMembers, setTicketTeamMembers] = useState<any[]>([]);
  const [analysen, setAnalysen] = useState<Record<string, EmailAnalyse>>({});
  const [rechnungStatus, setRechnungStatus] = useState<Record<string, EmailRechnungBadge>>({});
  const [ablageStatus, setAblageStatus] = useState<Record<string, boolean>>({});
  const [ticketsByEmail, setTicketsByEmail] = useState<Set<string>>(new Set());

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // #556: Ladungen durchnummerieren (veraltete Antworten verwerfen), Mailfenster lädt nur seine Mail,
  // Takt vergleicht nur eine Änderungs-Signatur (neueste updated_at + Anzahl).
  const ladeNr = useRef(0);
  const fensterMailId = useRef<string | null>(fenster ? (initialEmailId ?? null) : null);
  const aenderungsSignatur = useRef<string | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Deep-Link (z. B. aus der ⌘K-Suche oder dem Dashboard): Ziel-Mail kann in JEDEM Ordner
  // liegen (archiviert/werbung/gesendet/erledigt/posteingang) — nicht mehr stur auf den
  // Posteingang-Tab erzwingen, sonst wird sie dort nie gefunden. Stattdessen die Mail per ID
  // laden und den passenden Tab daraus ableiten; der Tab-Wechsel triggert den bestehenden
  // Fetch-Effekt, danach greift die Such-Logik im Effekt darunter.
  useEffect(() => {
    if (!initialEmailId) return;
    (async () => {
      const { data } = await supabase
        .from('emails')
        .select('id, richtung, erledigt, archiviert_am, ist_werbung, konto_id')
        .eq('id', initialEmailId)
        .maybeSingle();
      if (!data) { onClearInitial?.(); return; }
      let ziel: TabKey = 'posteingang';
      if (data.archiviert_am) ziel = 'archiviert';
      else if (data.ist_werbung) ziel = 'werbung';
      else if (data.richtung === 'ausgang') ziel = 'gesendet';
      else if (data.erledigt) ziel = 'erledigt';
      setActiveTab((cur) => (cur === ziel ? cur : ziel));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmailId]);

  // Sucht die Ziel-Mail erst, NACHDEM ein echter fetchEmails()-Durchlauf für das aktuell
  // gewählte Konto abgeschlossen ist (fetchAbgeschlossen). `loading` allein reicht nicht: es
  // startet mit false, BEVOR überhaupt ein Fetch lief — ein Guard auf `!loading` würde beim
  // allerersten Render (vor dem asynchronen fetchKonten/fetchEmails) sofort "nicht gefunden"
  // schließen und initialEmailId verwerfen, bevor die echten Daten überhaupt geladen sind.
  // fetchAbgeschlossen startet dagegen false und wird erst true, wenn fetchEmails() (auch im
  // Fehlerfall, siehe finally dort) wirklich einmal durchgelaufen ist.
  useEffect(() => {
    if (!initialEmailId) return;
    if (!fetchAbgeschlossen || loading) return;
    const found = emails.find((e) => e.id === initialEmailId);
    if (found) handleSelectEmail(found);
    onClearInitial?.();
  }, [initialEmailId, fetchAbgeschlossen, loading, emails]);

  // ── Persist filters to localStorage ──────────────────────────────────────

  useEffect(() => {
    localStorage.setItem('email_filters', JSON.stringify({
      tab: activeTab,
      gelesen: filterGelesen,
      zuordnung: filterZuordnung,
      triage: filterTriage,
      erledigt: filterErledigt,
      zustaendig: filterZustaendig,
      markiert: filterMarkiert,
      anhang: filterAnhang,
      kontoId: selectedKontoId,
    }));
  }, [activeTab, filterGelesen, filterZuordnung, filterTriage, filterErledigt, filterZustaendig, filterMarkiert, filterAnhang, selectedKontoId]);

  // Teams + Mitarbeiter für den Zuständigkeits-Filter (einmalig).
  useEffect(() => {
    (async () => {
      const [t, m] = await Promise.all([
        supabase.from('teams').select('id, name').order('name'),
        supabase.from('profiles').select('id, full_name').or('role.eq.team,role.eq.admin').eq('zuweisbar', true),
      ]);
      setTeams(t.data || []);
      setTeamMembers(m.data || []);
    })();
  }, []);

  // ── Debounced search ─────────────────────────────────────────────────────

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 250);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm]);

  // ── Fetch email accounts + ticket kategorien ─────────────────────────────

  useEffect(() => {
    fetchKonten();
    supabase.from('ticket_kategorien').select('id, name, farbe').eq('aktiv', true).order('sortierung')
      .then(({ data }) => { if (data) setTicketKategorien(data); });
  }, []);

  const fetchKonten = async () => {
    setKontenLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_konten')
        .select('*')
        .eq('aktiv', true);

      if (error) throw error;
      const accounts = data || [];
      setKonten(accounts);

      if (accounts.length > 0) {
        const savedId = savedState?.kontoId;
        const matchesSaved = accounts.find(k => k.id === savedId);
        setSelectedKontoId(matchesSaved ? savedId : accounts[0].id);
      }
    } catch (err) {
      console.error('Fehler beim Laden der E-Mail-Konten:', err);
    } finally {
      setKontenLoading(false);
    }
  };

  // ── Fetch emails when konto or tab changes ───────────────────────────────

  useEffect(() => {
    if (selectedKontoId && (activeTab === 'posteingang' || activeTab === 'offen' || activeTab === 'erledigt' || activeTab === 'wiedervorlage' || activeTab === 'rueckmeldung' || activeTab === 'gesendet' || activeTab === 'geplant' || activeTab === 'entwuerfe' || activeTab === 'werbung' || activeTab === 'archiviert' || activeTab === 'geloescht' || activeTab === 'objekt')) {
      fetchEmails();
    }
  }, [selectedKontoId, activeTab, selectedObjektId]);

  // ── Polling every 30s ────────────────────────────────────────────────────

  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    if (selectedKontoId && (activeTab === 'posteingang' || activeTab === 'offen' || activeTab === 'erledigt' || activeTab === 'wiedervorlage' || activeTab === 'rueckmeldung' || activeTab === 'gesendet' || activeTab === 'geplant' || activeTab === 'entwuerfe' || activeTab === 'werbung' || activeTab === 'archiviert' || activeTab === 'geloescht' || activeTab === 'objekt')) {
      // #556: nur nachladen, wenn sich im Postfach wirklich etwas geändert hat (2 Mini-Abfragen statt
      // ganzer Liste + ~170 Folgeabfragen alle 30 s). Im Mailfenster kein Takt.
      if (fenster) return;
      aenderungsSignatur.current = null;
      const pruefe = async () => {
        const [neueste, anzahl] = await Promise.all([
          supabase.from('emails').select('updated_at').eq('konto_id', selectedKontoId).order('updated_at', { ascending: false }).limit(1),
          supabase.from('emails').select('id', { count: 'exact', head: true }).eq('konto_id', selectedKontoId),
        ]);
        if (neueste.error || anzahl.error) return;
        const sig = `${(neueste.data?.[0] as { updated_at?: string } | undefined)?.updated_at ?? ''}|${anzahl.count ?? 0}`;
        if (aenderungsSignatur.current !== null && sig !== aenderungsSignatur.current) void fetchEmails(true);
        aenderungsSignatur.current = sig;
      };
      void pruefe();
      pollingRef.current = setInterval(() => { void pruefe(); }, 30000);
    }

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [selectedKontoId, activeTab]);

  // Lädt, welche E-Mails bereits mit einem Ticket verknüpft sind (entity_links).
  const fetchTicketsByEmail = async (ids: string[]) => {
    if (ids.length === 0) { setTicketsByEmail(new Set()); return; }
    // In Batches: .in() mit vielen UUIDs erzeugt sonst eine zu lange GET-URL (414 URI Too Long).
    const CHUNK = 100;
    const batches: string[][] = [];
    for (let i = 0; i < ids.length; i += CHUNK) batches.push(ids.slice(i, i + CHUNK));
    const results = await Promise.all(
      batches.map((b) => supabase
        .from('entity_links')
        .select('target_id')
        .eq('source_type', 'ticket')
        .eq('target_type', 'email')
        .eq('link_type', 'quelle')
        .in('target_id', b)),
    );
    const alle = new Set<string>();
    for (const { data } of results) for (const l of (data as any[]) || []) alle.add(l.target_id);
    setTicketsByEmail(alle);
  };

  const fetchEmails = async (silent = false) => {
    if (!selectedKontoId) return;
    const nr = ++ladeNr.current;
    if (!silent) setLoading(true);
    setFetchAbgeschlossen(false);

    try {
      // WICHTIG: html_inhalt NICHT laden — die Liste braucht nur die 80-Zeichen-Vorschau
      // aus text_inhalt. html_inhalt (~13 MB über alle Mails) wird erst im Detail-Panel
      // für die geöffnete Mail nachgeladen. Sonst zieht die Liste bei jedem Laden 13 MB.
      let query = supabase
        .from('emails')
        .select(LISTEN_SPALTEN)
        .eq('konto_id', selectedKontoId);
      if (activeTab === 'objekt') {
        // Objekt-Ordner: ALLE Mails des Objekts (Ein- + Ausgang), ohne Richtungs-/Ordner-Filter —
        // ABER geplante/noch nicht versendete Mails (SCHEDULED/SENDING) dürfen hier nicht als
        // bereits verschickt erscheinen (Leak-Fix, siehe Task-6-Review von Task 5).
        if (!selectedObjektId) { setEmails([]); return; }
        query = query.eq('objekt_id', selectedObjektId).not('ordner', 'in', '("SCHEDULED","SENDING")');
      } else if (activeTab === 'archiviert') {
        // Archiv: alle archivierten eingehenden Mails (unabhängig von Werbung/Erledigt).
        query = query.eq('richtung', 'eingang').not('archiviert_am', 'is', null);
      } else if (activeTab === 'geloescht') {
        // #544: Ordner „Gelöscht“ — alles, was gelöscht wurde (Ein- und Ausgang); leert sich nicht selbst.
        query = query.not('geloescht_am', 'is', null);
      } else {
        const richtung = (activeTab === 'gesendet' || activeTab === 'geplant' || activeTab === 'entwuerfe') ? 'ausgang' : 'eingang';
        // #552: Wiedervorlage auch für gesendete Mails — bis zum Termin im Ordner „Wiedervorlage“,
        // fällig dann oben in Posteingang/Offen (wie eingehende).
        const mitGesendeten = activeTab === 'posteingang' || activeTab === 'offen'
          ? `richtung.eq.eingang,and(ordner.eq.SENT,wiedervorlage_am.lte.${new Date().toISOString()})`
          : activeTab === 'wiedervorlage' ? 'richtung.eq.eingang,ordner.eq.SENT' : null;
        query = mitGesendeten ? query.or(mitGesendeten) : query.eq('richtung', richtung);
        // Archivierte Mails sind aus allen normalen Ordnern raus.
        if (activeTab !== 'gesendet' && activeTab !== 'geplant' && activeTab !== 'entwuerfe') query = query.is('archiviert_am', null);
        if (activeTab === 'posteingang') query = query.eq('ist_werbung', false);
        else if (activeTab === 'offen') query = query.eq('ist_werbung', false).eq('erledigt', false);
        else if (activeTab === 'erledigt') query = query.eq('ist_werbung', false).eq('erledigt', true);
        else if (activeTab === 'wiedervorlage') {
          query = query.gt('wiedervorlage_am', new Date().toISOString());
        }
        // #551: warten auf Antwort — älteste zuerst ist über das Etikett erkennbar
        else if (activeTab === 'rueckmeldung') query = query.not('rueckmeldung_seit', 'is', null);
        else if (activeTab === 'werbung') query = query.eq('ist_werbung', true);
        else if (activeTab === 'gesendet') query = query.eq('ordner', 'SENT');
        else if (activeTab === 'geplant') query = query.in('ordner', ['SCHEDULED', 'SENDING']);
        else if (activeTab === 'entwuerfe') query = query.eq('ordner', 'DRAFT');
        // Geschlummerte Wiedervorlagen (wiedervorlage_am in der Zukunft) sind aus den normalen
        // Ordnern raus — sie tauchen erst im Wiedervorlage-Ordner oder nach Fälligkeit wieder auf.
        if (activeTab === 'posteingang' || activeTab === 'offen' || activeTab === 'erledigt') {
          query = query.or(`wiedervorlage_am.is.null,wiedervorlage_am.lte.${new Date().toISOString()}`);
        }
      }
      // #543: Supabase liefert höchstens 1000 Zeilen je Abfrage — der Posteingang hat mehr,
      // ältere Mails fehlten in Liste und Suche. Seitenweise laden (id als stabiler Zweitschlüssel).
      // #544: Gelöschte Mails stehen nur im Ordner „Gelöscht“.
      if (activeTab !== 'geloescht') query = query.is('geloescht_am', null);
      // #556: Im Mailfenster nur DIE eine Mail laden — vorher lud jedes Fenster den ganzen Posteingang
      // (4.000+ Mails samt Folgeabfragen), bevor es die Mail zeigte (~10 s).
      // Ohne Ordner-Filter: auch archivierte, geschlummerte oder gelöschte Mails müssen sich öffnen.
      if (fensterMailId.current) query = supabase.from('emails').select(LISTEN_SPALTEN).eq('id', fensterMailId.current);
      const sortierteAbfrage = query.order('datum', { ascending: false }).order('id', { ascending: false });
      // Fällige Wiedervorlagen oben anpinnen — stabiler Sort erhält die Datums-Reihenfolge
      // innerhalb der beiden Gruppen (fällig / nicht fällig).
      const jetzt = new Date();
      const sortiere = (rows: any[]) => [...rows].sort((a: any, b: any) =>
        Number(istWiedervorlageFaellig(b.wiedervorlage_am, jetzt)) - Number(istWiedervorlageFaellig(a.wiedervorlage_am, jetzt))) as unknown as Email[];
      // #556: neueste 1.000 sofort zeigen, den Rest im Hintergrund nachladen.
      const SEITE = 1000;
      const { data: erste, error: ersteFehler } = await sortierteAbfrage.range(0, SEITE - 1);
      if (ersteFehler) throw ersteFehler;
      if (nr !== ladeNr.current) return; // inzwischen anderer Ordner/neuere Ladung
      let list = sortiere(erste ?? []);
      setEmails(list);
      if (!silent) setLoading(false);
      if ((erste ?? []).length === SEITE) {
        const rest = await alleSeiten<any>((von, bis) => sortierteAbfrage.range(von + SEITE, bis + SEITE));
        if (nr !== ladeNr.current) return;
        list = sortiere([...(erste ?? []), ...rest]);
        setEmails(list);
      }

      // Analysen + Rechnungs-Import-Status für sichtbare Mails laden
      if (list.length > 0) {
        ladeAnalysenFuerEmails(list.map((e) => e.id)).then(setAnalysen);
        ladeRechnungStatusFuerEmails(list.map((e) => e.id)).then(setRechnungStatus);
        ladeAblageStatusFuerEmails(list.map((e) => e.id)).then(setAblageStatus);
      } else {
        setAnalysen({});
        setRechnungStatus({});
        setAblageStatus({});
      }

      // Ticket-Verknüpfungen für sichtbare Mails laden
      fetchTicketsByEmail(list.map((e) => e.id));

      // Hinweis: unreadCount wird NICHT hier aus der Liste berechnet — sonst würde das
      // Öffnen des Werbung-Ordners (oder das 30s-Polling) das Posteingang-Badge mit der
      // Werbung-Zahl überschreiben. Der dedizierte fetchUnread-Effekt (unten, mit
      // .eq('ist_werbung', false)) ist die alleinige Quelle und läuft bei jeder
      // emails-Änderung.
    } catch (err) {
      console.error('Fehler beim Laden der E-Mails:', err);
    } finally {
      // Nur die jüngste Ladung meldet „fertig“ — eine verworfene darf das Deep-Link-Öffnen nicht auslösen.
      if (nr === ladeNr.current) {
        if (!silent) setLoading(false);
        setFetchAbgeschlossen(true);
      }
    }
  };

  // ── Ticket-Links neu laden, wenn im KI-Panel ein Ticket erstellt wurde ───

  useEffect(() => {
    if (ticketRefresh > 0 && emails.length > 0) {
      fetchTicketsByEmail(emails.map((e) => e.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketRefresh]);

  // ── Fetch unread count separately for badge ──────────────────────────────

  useEffect(() => {
    if (!selectedKontoId) return;
    const fetchUnread = async () => {
      const { count } = await supabase
        .from('emails')
        .select('id', { count: 'exact', head: true })
        .eq('konto_id', selectedKontoId)
        .is('geloescht_am', null)
        .eq('richtung', 'eingang')
        .eq('ist_werbung', false)
        .is('archiviert_am', null)
        .eq('gelesen', false)
        .or(`wiedervorlage_am.is.null,wiedervorlage_am.lte.${new Date().toISOString()}`);
      setUnreadCount(count || 0);
    };
    fetchUnread();
  }, [selectedKontoId, emails]);

  const [werbungCount, setWerbungCount] = useState(0);
  useEffect(() => {
    if (!selectedKontoId) return;
    (async () => {
      const { count } = await supabase
        .from('emails')
        .select('id', { count: 'exact', head: true })
        .eq('konto_id', selectedKontoId)
        .is('geloescht_am', null)
        .eq('richtung', 'eingang')
        .eq('ist_werbung', true)
        .is('archiviert_am', null);
      setWerbungCount(count || 0);
    })();
  }, [selectedKontoId, emails]);

  const [archiviertCount, setArchiviertCount] = useState(0);
  useEffect(() => {
    if (!selectedKontoId) { setArchiviertCount(0); return; }
    (async () => {
      const { count } = await supabase
        .from('emails')
        .select('id', { count: 'exact', head: true })
        .eq('konto_id', selectedKontoId)
        .is('geloescht_am', null)
        .eq('richtung', 'eingang')
        .not('archiviert_am', 'is', null);
      setArchiviertCount(count || 0);
    })();
  }, [selectedKontoId, emails]);

  const [offenCount, setOffenCount] = useState(0);
  const fetchOffenCount = async () => {
    if (!selectedKontoId) { setOffenCount(0); return; }
    const { count } = await supabase
      .from('emails')
      .select('id', { count: 'exact', head: true })
      .eq('konto_id', selectedKontoId)
      .is('geloescht_am', null)
      .eq('richtung', 'eingang')
      .eq('ist_werbung', false)
      .is('archiviert_am', null)
      .eq('erledigt', false)
      .or(`wiedervorlage_am.is.null,wiedervorlage_am.lte.${new Date().toISOString()}`);
    setOffenCount(count || 0);
  };
  useEffect(() => { fetchOffenCount(); }, [selectedKontoId, emails]);

  // ── Wiedervorlage-Zähler (Badge) — fällige zählen NICHT mit (die stehen bereits
  // wieder in Posteingang/Offen), nur die noch geschlummerten. ─────────────────
  const [wiedervorlageCount, setWiedervorlageCount] = useState(0);
  const fetchWiedervorlageCount = async () => {
    if (!selectedKontoId) { setWiedervorlageCount(0); return; }
    const { count } = await supabase
      .from('emails')
      .select('id', { count: 'exact', head: true })
      .eq('konto_id', selectedKontoId)
      .is('geloescht_am', null)
      .or('richtung.eq.eingang,ordner.eq.SENT')
      .gt('wiedervorlage_am', new Date().toISOString());
    setWiedervorlageCount(count || 0);
  };
  useEffect(() => { fetchWiedervorlageCount(); }, [selectedKontoId, emails]);

  // #553: Welche Eingangsmails sind schon beantwortet? Gesendete Mails im selben Verlauf.
  const [antworten, setAntworten] = useState<Map<string, LetzteAntwort>>(new Map());
  useEffect(() => {
    const convs = [...new Set(emails.filter(e => e.richtung === 'eingang' && e.conversation_id).map(e => e.conversation_id as string))];
    if (!convs.length) { setAntworten(new Map()); return; }
    let lebt = true;
    (async () => {
      const teile: string[][] = [];
      for (let i = 0; i < convs.length; i += 100) teile.push(convs.slice(i, i + 100));
      const ergebnisse = await Promise.all(teile.map(t => supabase.from('emails').select('conversation_id, datum, user_id')
        .eq('richtung', 'ausgang').eq('ordner', 'SENT').in('conversation_id', t)));
      if (!lebt) return;
      setAntworten(letzteAntwortJeVerlauf(ergebnisse.flatMap(r => (r.data ?? []) as { conversation_id: string | null; datum: string | null; user_id: string | null }[])));
    })();
    return () => { lebt = false; };
  }, [emails]);

  // #551: Rückmeldung-Zähler
  const [rueckmeldungCount, setRueckmeldungCount] = useState(0);
  useEffect(() => {
    if (!selectedKontoId) { setRueckmeldungCount(0); return; }
    void supabase.from('emails').select('id', { count: 'exact', head: true })
      .eq('konto_id', selectedKontoId).is('geloescht_am', null).not('rueckmeldung_seit', 'is', null)
      .then(({ count }) => setRueckmeldungCount(count || 0));
  }, [selectedKontoId, emails]);
  const setzeRueckmeldung = async (em: Email, an: boolean) => {
    const patch = an ? { rueckmeldung_seit: new Date().toISOString(), rueckmeldung_von: meineId } : { rueckmeldung_seit: null, rueckmeldung_von: null };
    const { error } = await supabase.from('emails').update(patch).eq('id', em.id);
    if (error) { zeigeToast('Fehlgeschlagen: ' + error.message, true); return; }
    setEmails(prev => activeTab === 'rueckmeldung' && !an ? prev.filter(x => x.id !== em.id) : prev.map(x => x.id === em.id ? { ...x, rueckmeldung_seit: patch.rueckmeldung_seit } : x));
    zeigeToast(an ? 'Wartet auf Rückmeldung – steht im Ordner „Rückmeldung“' : 'Rückmeldung erhalten');
  };

  // ── Entwürfe-Zähler (Badge) ──────────────────────────────────────────────
  const [entwuerfeCount, setEntwuerfeCount] = useState(0);
  const fetchEntwuerfeCount = async () => {
    if (!selectedKontoId) { setEntwuerfeCount(0); return; }
    const { count } = await supabase
      .from('emails')
      .select('id', { count: 'exact', head: true })
      .eq('konto_id', selectedKontoId)
      .is('geloescht_am', null)
      .eq('richtung', 'ausgang')
      .eq('ordner', 'DRAFT');
    setEntwuerfeCount(count || 0);
  };
  useEffect(() => {
    fetchEntwuerfeCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKontoId, emails]);

  // ── Geplant-Zähler (Badge) — SCHEDULED + SENDING (Versand läuft gerade) ──
  const [geplantCount, setGeplantCount] = useState(0);
  const fetchGeplantCount = async () => {
    if (!selectedKontoId) { setGeplantCount(0); return; }
    const { count } = await supabase
      .from('emails')
      .select('id', { count: 'exact', head: true })
      .eq('konto_id', selectedKontoId)
      .is('geloescht_am', null)
      .eq('richtung', 'ausgang')
      .in('ordner', ['SCHEDULED', 'SENDING']);
    setGeplantCount(count || 0);
  };
  useEffect(() => {
    fetchGeplantCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKontoId, emails]);

  // Objekte (alle) + Ungelesen-Zähler je Objekt für die Sub-Nav laden.
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('objekte').select('id, objektnummer, bezeichnung').order('objektnummer');
      setObjekte((data as any[]) || []);
    })();
  }, []);
  useEffect(() => {
    if (!selectedKontoId) { setObjektUnread({}); return; }
    (async () => {
      const { data } = await supabase.from('emails')
        .select('objekt_id')
        .eq('konto_id', selectedKontoId).eq('richtung', 'eingang').is('geloescht_am', null)
        .eq('gelesen', false).not('objekt_id', 'is', null);
      const map: Record<string, number> = {};
      for (const r of (data as any[]) || []) { const id = r.objekt_id as string; map[id] = (map[id] || 0) + 1; }
      setObjektUnread(map);
    })();
  }, [selectedKontoId, emails]);

  // ── Entwurf öffnen: vollständige Zeile laden und im Verfassen-Formular fortsetzen ──
  const oeffneEntwurf = async (email: Email) => {
    const konto = konten.find((k) => k.id === selectedKontoId);
    if (!konto) return;
    const { data } = await supabase
      .from('emails')
      .select('id, an, cc, bcc, betreff, html_inhalt, text_inhalt, entwurf_anhaenge')
      .eq('id', email.id)
      .maybeSingle();
    if (!data) return;
    oeffneVerfassen({
        titel: 'Entwurf',
        nachher: () => { setTimeout(() => { fetchEmails(true); fetchEntwuerfeCount(); }, 1200); },
        draft: {
          id: data.id,
          an: data.an || [],
          cc: data.cc || [],
          bcc: data.bcc || [],
          betreff: data.betreff || '',
          text_inhalt: data.text_inhalt || '',
          html_inhalt: data.html_inhalt || '',
          anhaenge: ((data.entwurf_anhaenge as any[]) || []).map((a) => ({ name: a.name, size: a.size, path: a.path })),
        },
    });
  };

  // ── Antworten/Weiterleiten öffnen ────────────────────────────────────────
  // Die Listen-Query lädt html_inhalt bewusst NICHT (Performance) — für das Zitat
  // der Ursprungsmail hier die Inhalte gezielt nachladen (Muster oeffneEntwurf).
  // Ohne das bleibt der Zitat-Block bei HTML-only-Mails (Graph-Sync) leer.
  const oeffneAntwortOderWeiterleitung = async (email: Email, art: 'reply' | 'replyAll' | 'forward') => {
    let quelle: Email = email;
    if (!email.text_inhalt && !email.html_inhalt) {
      const { data } = await supabase
        .from('emails')
        .select('html_inhalt, text_inhalt')
        .eq('id', email.id)
        .maybeSingle();
      if (data) quelle = { ...email, html_inhalt: data.html_inhalt, text_inhalt: data.text_inhalt };
    }
    oeffneVerfassen(art === 'forward'
      ? { forward: forwardKontextAusEmail(quelle) }
      : { replyTo: replyKontextAusEmail(quelle, art === 'replyAll'), antwortAuf: email.richtung === 'eingang' ? email : undefined });
  };

  // ── Toggle markiert ──────────────────────────────────────────────────────

  const toggleMarkiert = async (e: React.MouseEvent, email: Email) => {
    e.stopPropagation();
    try {
      await supabase.from('emails').update({ markiert: !email.markiert }).eq('id', email.id);
      setEmails(prev => prev.map(em => em.id === email.id ? { ...em, markiert: !em.markiert } : em));
      if (selectedEmail?.id === email.id) {
        setSelectedEmail({ ...email, markiert: !email.markiert });
      }
    } catch (err) {
      console.error('Fehler beim Markieren:', err);
    }
  };

  // ── Toggle erledigt (offen ↔ erledigt) — nur eingehende E-Mails ──────────
  const toggleErledigt = async (email: Email, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const neu = !email.erledigt;
    // Als erledigt markiert hebt eine offene Wiedervorlage automatisch auf (Spec).
    const update: { erledigt: boolean; wiedervorlage_am?: null; in_bearbeitung_von?: null; in_bearbeitung_seit?: null } = { erledigt: neu };
    // Erledigt hebt Wiedervorlage und „In Bearbeitung“ (#527) auf.
    if (neu) { update.wiedervorlage_am = null; update.in_bearbeitung_von = null; update.in_bearbeitung_seit = null; }
    // #573: Protokoll setzt der Trigger; für die Anzeige gleich mitführen.
    const protokoll = neu
      ? { erledigt_am: new Date().toISOString(), erledigt_von: userProfile?.id ?? null, erledigt_automatisch: null }
      : { erledigt_am: null, erledigt_von: null, erledigt_automatisch: null };
    try {
      await supabase.from('emails').update(update).eq('id', email.id);
      if (activeTab === 'offen' || activeTab === 'erledigt') {
        // Gehört nicht mehr in den aktuellen Ordner → aus der Liste (und ggf. Detail) entfernen.
        setEmails(prev => prev.filter(em => em.id !== email.id));
        if (selectedEmail?.id === email.id) setSelectedEmail(null);
      } else {
        setEmails(prev => prev.map(em => em.id === email.id ? { ...em, ...update, ...protokoll } : em));
        if (selectedEmail?.id === email.id) setSelectedEmail({ ...email, ...update, ...protokoll });
      }
      // offenCount aktualisiert der reaktive Effekt auf [selectedKontoId, emails] mit.
    } catch (err) {
      console.error('Erledigt umschalten fehlgeschlagen:', err);
    }
  };

  // ── Kontextmenü-Aktionen (Rechtsklick auf eine E-Mail) ──
  const toggleStern = async (email: Email) => {
    try {
      await supabase.from('emails').update({ markiert: !email.markiert }).eq('id', email.id);
      setEmails(prev => prev.map(em => em.id === email.id ? { ...em, markiert: !em.markiert } : em));
      if (selectedEmail?.id === email.id) setSelectedEmail({ ...selectedEmail, markiert: !email.markiert });
    } catch (err) { console.error('Stern umschalten fehlgeschlagen:', err); }
  };
  const toggleGelesen = async (email: Email) => {
    const neu = !email.gelesen;
    try {
      await supabase.from('emails').update({ gelesen: neu }).eq('id', email.id);
      setEmails(prev => prev.map(em => em.id === email.id ? { ...em, gelesen: neu } : em));
      if (selectedEmail?.id === email.id) setSelectedEmail({ ...selectedEmail, gelesen: neu });
    } catch (err) { console.error('Gelesen umschalten fehlgeschlagen:', err); }
  };
  // #544: Löschen verschiebt in den Ordner „Gelöscht“; dort heißt Löschen endgültig.
  // Endgültig löschen dürfen laut RLS nur Admins — ohne Recht filtert die DB still (0 Zeilen),
  // darum die betroffenen Zeilen zurückholen und zählen.
  const loescheEmails = async (ids: string[], endgueltig: boolean): Promise<{ error: { message: string } | null }> => {
    const { data, error } = endgueltig
      ? await supabase.from('emails').delete().in('id', ids).select('id')
      : await supabase.from('emails').update({ geloescht_am: new Date().toISOString(), geloescht_von: meineId }).in('id', ids).select('id');
    if (error) return { error };
    if ((data ?? []).length === 0) return { error: { message: endgueltig ? 'Endgültig löschen dürfen nur Admins.' : 'Keine Berechtigung.' } };
    return { error: null };
  };
  const deleteEmail = async (email: Email) => {
    try {
      const { error } = await loescheEmails([email.id], !!email.geloescht_am);
      if (error) { zeigeToast('Löschen fehlgeschlagen: ' + error.message, true); return; }
      setEmails(prev => prev.filter(em => em.id !== email.id));
      if (selectedEmail?.id === email.id) setSelectedEmail(null);
    } catch (err) { console.error('E-Mail löschen fehlgeschlagen:', err); }
  };
  // ── Geplant-Ordner: "Jetzt senden" stößt die Edge Function für genau diese eine
  // Zeile an (deren CAS-Claim schützt vor einem gleichzeitigen Cron-Lauf); "Abbrechen"
  // wandelt die geplante Mail zurück in einen Entwurf. Beide refetchen die Liste danach —
  // Geplant-/Entwürfe-Zähler aktualisiert der reaktive Effekt auf [selectedKontoId, emails] mit.
  const jetztSenden = async (email: Email) => {
    try {
      // Retry-Zähler + geplant_am zurücksetzen (Fix 3): eine Zeile, die den Backoff bereits
      // 5x durchlaufen hat, steht mit geplant_am=null da (Cron-Query greift dann nicht mehr) —
      // ohne dieses Reset würde ein manueller "Jetzt senden"-Klick sofort wieder im Backoff-Pfad
      // landen bzw. beim nächsten Fehlversuch sofort endgültig aufgeben.
      await supabase.from('emails')
        .update({ versand_versuche: 0, geplant_am: new Date().toISOString() })
        .eq('id', email.id).eq('ordner', 'SCHEDULED');
      const { error } = await supabase.functions.invoke('email-geplant-versand', { body: { email_id: email.id } });
      if (error) throw error;
    } catch (err) {
      console.error('Jetzt senden fehlgeschlagen:', err);
    } finally {
      fetchEmails(true);
    }
  };
  const geplantenVersandAbbrechen = async (email: Email) => {
    try {
      // Rohen Editor-Body (entwurf_html, Fix 2) vorher laden: html_inhalt der SCHEDULED-Zeile
      // ist der FERTIG gerenderte Body (Briefpapier+Signatur bereits eingesetzt) — würde er beim
      // erneuten Öffnen als DRAFT unverändert wieder durch baueMailHtml gejagt, entstünde eine
      // doppelte Signatur/verschachteltes Briefpapier. Alt-Zeilen ohne entwurf_html (vor diesem
      // Fix angelegt): Fallback auf das bisherige Verhalten, html_inhalt bleibt wie es ist.
      const { data: zeile } = await supabase.from('emails').select('entwurf_html').eq('id', email.id).maybeSingle();
      const update: { ordner: string; geplant_am: null; html_inhalt?: string; entwurf_html?: null } = {
        ordner: 'DRAFT', geplant_am: null,
      };
      if (zeile?.entwurf_html != null) {
        update.html_inhalt = zeile.entwurf_html;
        update.entwurf_html = null;
      }
      // CAS wie bei der Cron-Function: nur greifen, solange die Zeile noch SCHEDULED ist.
      // Läuft der Versand gerade (ordner='SENDING', ein Cron-Tick oder "Jetzt senden" hat sie
      // bereits geclaimt), darf "Abbrechen" NICHT dazwischenfunken — sonst würde die laufende
      // Function die Zeile nach erfolgreichem Graph-Versand nicht mehr auf SENT setzen können
      // (ihr eigenes CAS-Update träfe die inzwischen auf DRAFT gesetzte Zeile nicht mehr) und
      // ein späterer erneuter Versand würde die Mail doppelt verschicken.
      await supabase.from('emails').update(update).eq('id', email.id).eq('ordner', 'SCHEDULED');
    } catch (err) {
      console.error('Geplanten Versand abbrechen fehlgeschlagen:', err);
    } finally {
      if (selectedEmail?.id === email.id) setSelectedEmail(null);
      fetchEmails(true);
    }
  };

  useEffect(() => {
    if (!emailMenu) return;
    const close = () => setEmailMenu(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setEmailMenu(null); };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [emailMenu]);

  // ── Select email and mark as read ────────────────────────────────────────

  const handleSelectEmail = async (email: Email) => {
    // #551: am Desktop eigenes Browserfenster je Mail — das Postfach bleibt stehen.
    // Mobil (keine Pop-ups) und im Mailfenster selbst wie bisher im Lesefenster.
    // #572: nur EIN Mailfenster – ist es offen, wechselt es zur angeklickten Mail (ohne Neuladen),
    // sonst wird es geöffnet bzw. das gleichnamige Fenster wiederverwendet.
    let eigenesFenster: Window | null = null;
    if (!fenster && !mobil) {
      const offen = mailFensterRef.current && !mailFensterRef.current.closed ? mailFensterRef.current : null;
      if (offen) {
        kanalRef.current?.postMessage({ typ: 'zeige', id: email.id });
        eigenesFenster = offen;
      } else {
        eigenesFenster = window.open(mailFensterPfad(email.id), MAIL_FENSTER_NAME, MAIL_FENSTER_MERKMALE);
        mailFensterRef.current = eigenesFenster;
      }
    }
    if (eigenesFenster) eigenesFenster.focus(); else setSelectedEmail(email);

    if (!email.gelesen && email.richtung === 'eingang') {
      try {
        await supabase.from('emails').update({ gelesen: true }).eq('id', email.id);
        setEmails(prev => prev.map(em => em.id === email.id ? { ...em, gelesen: true } : em));
      } catch (err) {
        console.error('Fehler beim Markieren als gelesen:', err);
      }
    }

    // Öffnen einer FÄLLIGEN Wiedervorlage hebt sie automatisch auf (Spec).
    if (istWiedervorlageFaellig(email.wiedervorlage_am, new Date())) {
      try {
        await supabase.from('emails').update({ wiedervorlage_am: null }).eq('id', email.id);
        setEmails(prev => prev.map(em => em.id === email.id ? { ...em, wiedervorlage_am: null } : em));
        setSelectedEmail(prev => prev && prev.id === email.id ? { ...prev, wiedervorlage_am: null } : prev);
      } catch (err) {
        console.error('Wiedervorlage automatisch aufheben fehlgeschlagen:', err);
      }
    }
  };

  // ── Filter emails ────────────────────────────────────────────────────────

  const filteredEmails = emails.filter(email => {
    // Gelesen filter
    if (filterGelesen === 'gelesen' && !email.gelesen) return false;
    if (filterGelesen === 'ungelesen' && email.gelesen) return false;

    // Zuordnung filter
    if (filterZuordnung === 'mit' && !email.mieter_id && !email.eigentuemer_id) return false;
    if (filterZuordnung === 'ohne' && (email.mieter_id || email.eigentuemer_id)) return false;

    // Triage filter (nur im Posteingang relevant)
    if (activeTab === 'posteingang' && filterTriage !== 'alle') {
      const analyse = analysen[email.id];
      if (filterTriage === 'ohne_ticket' && !(analyse && istTriagierbar(analyse, ticketsByEmail.has(email.id)))) return false;
      if (filterTriage === 'notfall' && !analyse?.ist_notfall) return false;
    }

    // Offen/Erledigt filter
    if (filterErledigt === 'offen' && email.erledigt) return false;
    if (filterErledigt === 'erledigt' && !email.erledigt) return false;

    // Markiert / Anhänge
    if (filterMarkiert && !email.markiert) return false;
    if (filterAnhang && !email.hat_anhaenge) return false;

    // Zuständigkeit (Team/Person des zugeordneten Objekts)
    if (filterZustaendig !== 'alle') {
      const effId = email.zustaendig_id ?? email.objekt?.zustaendig_id;
      const effTyp = (email.zustaendig_id ? email.zustaendig_typ : email.objekt?.zustaendig_typ) ?? null;
      if (!matchtZustaendigkeitsFilter(filterZustaendig, effId, effTyp as ZustaendigTyp)) return false;
    }

    // Search
    // Suche (#543): auch Absendername, Cc, verknüpfter Mieter/Eigentümer/Objekt und Text; ö = oe.
    if (debouncedSearch) {
      return mailTrifft({
        betreff: email.betreff, von: email.von, von_name: email.von_name,
        an: empfaengerAlsText(email.an), cc: empfaengerAlsText(email.cc), text_inhalt: email.text_inhalt,
        namen: [
          email.mieter ? [email.mieter.vorname, email.mieter.nachname, email.mieter.firma_name].filter(Boolean).join(' ') : null,
          email.eigentuemer ? [email.eigentuemer.vorname, email.eigentuemer.nachname, email.eigentuemer.firma_name].filter(Boolean).join(' ') : null,
          email.objekt?.bezeichnung,
        ],
      }, debouncedSearch);
    }

    return true;
  });

  const hasActiveFilters = filterGelesen !== 'alle' || filterZuordnung !== 'alle' || filterTriage !== 'alle' || filterErledigt !== 'alle' || filterZustaendig !== 'alle' || filterMarkiert || filterAnhang || searchTerm !== '';

  // ── Tastatur-Navigation: ↑/↓ oder j/k durch die Liste, öffnet direkt ──
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (activeTab !== 'posteingang' && activeTab !== 'offen' && activeTab !== 'erledigt' && activeTab !== 'gesendet' && activeTab !== 'werbung' && activeTab !== 'archiviert') return;
      const el = document.activeElement as HTMLElement | null;
      const tag = (el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) return;
      const down = e.key === 'ArrowDown' || e.key === 'j';
      const up = e.key === 'ArrowUp' || e.key === 'k';
      if (!down && !up) return;
      if (filteredEmails.length === 0) return;
      e.preventDefault();
      const idx = selectedEmail ? filteredEmails.findIndex(em => em.id === selectedEmail.id) : -1;
      let next = down ? idx + 1 : idx - 1;
      if (next < 0) next = 0;
      if (next > filteredEmails.length - 1) next = filteredEmails.length - 1;
      const target = filteredEmails[next];
      if (target) {
        handleSelectEmail(target);
        requestAnimationFrame(() => {
          document.querySelector(`[data-email-id="${target.id}"]`)?.scrollIntoView({ block: 'nearest' });
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEmails, selectedEmail, activeTab]);

  // ↑/↓-Knöpfe im Lesefenster: gleiche Reihenfolge wie die Tastatur-Navigation (gefilterte Liste).
  const lesenIdx = selectedEmail ? filteredEmails.findIndex(em => em.id === selectedEmail.id) : -1;
  const geheZuMail = (i: number) => {
    const ziel = filteredEmails[i];
    if (!ziel) return;
    void handleSelectEmail(ziel);
    requestAnimationFrame(() => document.querySelector(`[data-email-id="${ziel.id}"]`)?.scrollIntoView({ block: 'nearest' }));
  };

  const resetFilters = () => {
    setFilterGelesen('alle');
    setFilterZuordnung('alle');
    setFilterTriage('alle');
    setFilterErledigt('alle');
    setFilterZustaendig('alle');
    setFilterMarkiert(false);
    setFilterAnhang(false);
    setSearchTerm('');
    setDebouncedSearch('');
  };

  // ── Triage-Zähler (Posteingang) ──────────────────────────────────────────

  const triageOhneTicketCount = emails.filter((email) => {
    const analyse = analysen[email.id];
    return !!analyse && istTriagierbar(analyse, ticketsByEmail.has(email.id));
  }).length;
  const triageNotfallCount = emails.filter((email) => !!analysen[email.id]?.ist_notfall).length;

  // ── Zuordnung badge helper ───────────────────────────────────────────────

  const getZuordnungBadge = (email: Email): ZuordnungsChip[] => {
    const badges: ZuordnungsChip[] = [];
    if (email.mieter) {
      const name = email.mieter.firma_name?.trim() || `${email.mieter.vorname} ${email.mieter.nachname}`.trim();
      badges.push({ art: 'mieter', label: `Mieter: ${name}` });
      const einheit = email.mieter.einheit?.trim();
      if (einheit) badges.push({ art: 'einheit', label: `Einheit: ${einheit}` });
    }
    if (email.eigentuemer) {
      const name = email.eigentuemer.firma_name?.trim() || `${email.eigentuemer.vorname} ${email.eigentuemer.nachname}`.trim();
      badges.push({ art: 'eigentuemer', label: `Eigent.: ${name}` });
    }
    if (email.objekt) badges.push({ art: 'objekt', label: email.objekt.bezeichnung });
    return badges;
  };

  // ── Rechnungs-Import-Badge (aus der Rechnungsablage) ─────────────────────
  const getRechnungBadge = (email: Email): ZeilenBadge | null => {
    const s = rechnungStatus[email.id];
    if (s === 'rechnung') return { art: 'rechnung', label: 'KI-Rechnung erfasst' };
    if (s === 'unklar') return { art: 'kipool', label: 'KI-Pool · prüfen' };
    // Duplikat sichtbar machen: sonst wirkt eine erneut geschickte Rechnung wie „nicht erkannt".
    if (s === 'duplikat') return { art: 'duplikat', label: 'Bereits erfasst' };
    // 'fehler' bewusst kein Badge (aktuell 184 Fälle -> würde die Liste mit Rot zufluten).
    return null;
  };

  // ── Dokument-Ablage-Badge (halbautomatische Drive-Ablage) ────────────────
  const getAblageBadge = (email: Email): ZeilenBadge | null =>
    ablageStatus[email.id] ? { art: 'abgelegt', label: 'Dokument abgelegt' } : null;

  // ── Zuständigkeits-Badge (Team/Person des zugeordneten Objekts) ──────────
  const getZustaendigBadge = (email: Email): ZeilenBadge | null => {
    const hasOverride = !!email.zustaendig_id;
    const typ = hasOverride ? (email.zustaendig_typ ?? null) : (email.objekt?.zustaendig_typ ?? null);
    const name = hasOverride
      ? resolveZustaendig(email.zustaendig_id, email.zustaendig_typ as ZustaendigTyp, teamMembers, teams)?.name
      : email.objekt?.zustaendig;
    if (!name || name === 'Global') return null;
    const eff = effektiveZustaendigkeit(email);
    return { art: typ === 'team' ? 'team' : 'person', label: name, farbe: eff ? farben.get(eff.id) : undefined };
  };

  // ── #527: „In Bearbeitung“ – wer die Mail gerade bearbeitet ─────────────
  const getBearbeitungBadge = (email: Email): ZeilenBadge | null => {
    const b = aktiveBearbeitung(email, new Date());
    if (!b) return null;
    const wer = b.vonId === meineId ? 'dir' : kurzname(teamMembers.find(m => m.id === b.vonId)?.full_name);
    return { art: 'bearbeitung', label: `In Bearbeitung · ${wer} · ${seitText(b.seit, new Date())}` };
  };

  const aktualisiereLokal = (id: string, patch: Partial<Email>) => {
    setEmails(prev => prev.map(em => (em.id === id ? { ...em, ...patch } : em)));
    if (selectedEmail?.id === id) setSelectedEmail(s => (s ? { ...s, ...patch } : s));
  };

  // Bedingtes Übernehmen: nur wenn frei, schon meine oder abgelaufen – sonst Hinweis, wer sie hat.
  const bearbeitungNehmen = async (em: Email, erzwingen = false) => {
    if (!meineId) return;
    const jetzt = new Date();
    const patch = { in_bearbeitung_von: meineId, in_bearbeitung_seit: jetzt.toISOString() };
    let q = supabase.from('emails').update(patch).eq('id', em.id);
    if (!erzwingen) q = q.or(`in_bearbeitung_von.is.null,in_bearbeitung_von.eq.${meineId},in_bearbeitung_seit.lt."${ablaufGrenze(jetzt)}"`);
    const { data, error } = await q.select('id');
    if (error) { zeigeToast('In Bearbeitung setzen fehlgeschlagen: ' + error.message, true); return; }
    if (!data?.length) {
      const { data: frisch } = await supabase.from('emails').select('in_bearbeitung_von, in_bearbeitung_seit').eq('id', em.id).maybeSingle();
      const wer = kurzname(teamMembers.find(m => m.id === frisch?.in_bearbeitung_von)?.full_name);
      if (frisch) aktualisiereLokal(em.id, frisch);
      zeigeToast(`${wer} bearbeitet diese Mail bereits – zum Übernehmen erneut rechtsklicken.`, true);
      return;
    }
    aktualisiereLokal(em.id, patch);
    zeigeToast('In Bearbeitung – Kolleginnen sehen das in der Liste');
  };

  const bearbeitungBeenden = async (em: Email) => {
    const patch = { in_bearbeitung_von: null, in_bearbeitung_seit: null };
    const { error } = await supabase.from('emails').update(patch).eq('id', em.id);
    if (error) { zeigeToast('Fehlgeschlagen: ' + error.message, true); return; }
    aktualisiereLokal(em.id, patch);
  };

  const setzeZustaendig = async (em: Email, wahl: { id: string; typ: 'team' | 'mitarbeiter' } | null) => {
    const patch = { zustaendig_id: wahl?.id ?? null, zustaendig_typ: wahl?.typ ?? null };
    const { error } = await supabase.from('emails').update(patch).eq('id', em.id);
    if (error) { zeigeToast('Zuweisen fehlgeschlagen: ' + error.message, true); return; }
    aktualisiereLokal(em.id, patch);
    const name = wahl ? (wahl.typ === 'team' ? teams.find(t => t.id === wahl.id)?.name : teamMembers.find(m => m.id === wahl.id)?.full_name) : null;
    zeigeToast(name ? `Zugewiesen an ${name}` : 'Zuweisung entfernt');
  };

  // ── Offen/Erledigt-Stempel — nur im Posteingang, nur eingehende ──────────
  const getErledigtBadge = (email: Email): ZeilenBadge | null => {
    if (activeTab !== 'posteingang' || email.richtung !== 'eingang') return null;
    return email.erledigt ? { art: 'erledigt', label: 'Erledigt' } : { art: 'offen', label: 'Offen' };
  };

  // ── Wiedervorlage-Badge — fällig überall golden markiert, im Wiedervorlage-Ordner
  // stattdessen mit dem geplanten Datum (dort ist per Filter ohnehin nichts fällig). ──
  const getWiedervorlageBadge = (email: Email): ZeilenBadge | null => {
    if (!email.wiedervorlage_am) return null;
    if (activeTab === 'wiedervorlage' || (activeTab === 'gesendet' && !istWiedervorlageFaellig(email.wiedervorlage_am, new Date()))) {
      const datum = new Date(email.wiedervorlage_am).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
      return { art: 'wiedervorlage', label: `Wiedervorlage: ${datum}` };
    }
    return istWiedervorlageFaellig(email.wiedervorlage_am, new Date()) ? { art: 'wiedervorlage', label: 'Wiedervorlage fällig' } : null;
  };

  // #553: „Beantwortet · Sarah · 10:42“ — auch in „Offen“, damit niemand doppelt antwortet.
  const getBeantwortetBadge = (email: Email): ZeilenBadge | null => {
    const a = antwortZu(email, antworten);
    if (!a) return null;
    const wer = a.user_id === meineId ? 'dir' : kurzname(teamMembers.find(m => m.id === a.user_id)?.full_name);
    return { art: 'beantwortet', label: `Beantwortet${wer ? ` · ${wer === 'dir' ? 'von dir' : wer}` : ''} · ${antwortZeit(a.datum, new Date())}` };
  };

  // #551: Rückmeldung-Etikett, ab 7 Tagen rot
  const getRueckmeldungBadge = (email: Email): ZeilenBadge | null => {
    const e = rueckmeldungEtikett(email.rueckmeldung_seit, new Date());
    if (!e) return null;
    return { art: 'rueckmeldung', label: e.label, ...(e.ueberfaellig ? { farbe: { fg: 'var(--bad)', bg: 'var(--bad-soft)' } } : {}) };
  };

  // ── Geplant-Badge — zeigt den geplanten Versandzeitpunkt (Format TT.MM. HH:MM) bzw.
  // "Wird gesendet…", solange die Zeile gerade von der Cron-Function beansprucht ist (SENDING). ──
  const getGeplantBadge = (email: Email): ZeilenBadge | null => {
    if (activeTab !== 'geplant') return null;
    if (email.ordner === 'SENDING') return { art: 'sendet', label: 'Wird gesendet…' };
    // Kein geplant_am bei ordner='SCHEDULED' = Retry-Cap erreicht (Fix 3, ab Versuch 5 gibt der
    // Cron endgültig auf) — die Zeile bleibt sichtbar im Geplant-Ordner, aber ohne Zeitpunkt.
    if (!email.geplant_am) return { art: 'fehlgeschlagen', label: 'Fehlgeschlagen' };
    const d = new Date(email.geplant_am);
    const tag = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
    const zeit = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    return { art: 'geplant', label: `Geplant: ${tag} ${zeit}` };
  };

  // ── Selected konto ──────────────────────────────────────────────────────

  const selectedKonto = konten.find(k => k.id === selectedKontoId);

  // E-Mail per Drag&Drop einem Objekt zuordnen (manuelle Zuordnung, wie im Detail-Panel).
  // #578: Mails per Maus auf einen Ordner ziehen – z. B. aus Werbung nach „Offen“.
  const verschiebeInOrdner = async (ids: string[], ziel: Exclude<EmailOrdner, 'objekt'>) => {
    const jetzt = new Date().toISOString();
    let fehler: string | null = null;
    let anzahl = 0;
    for (const id of ids) {
      const em = emails.find(x => x.id === id);
      if (!em || em.richtung !== 'eingang') continue;
      const aenderung = ziehAenderung(ziel, em, jetzt);
      if (!aenderung) continue;
      try {
        if (aenderung.werbung !== undefined) await setzeWerbungManuell(id, aenderung.werbung);
        if (Object.keys(aenderung.patch).length > 0) {
          const { error } = await supabase.from('emails').update(aenderung.patch).eq('id', id);
          if (error) throw error;
        }
        anzahl++;
      } catch (err: any) {
        fehler = err?.message || 'Unbekannt';
      }
    }
    if (selectedEmail && ids.includes(selectedEmail.id)) setSelectedEmail(null);
    if (ids.length > 1) clearAuswahl();
    await fetchEmails(true);
    if (fehler) zeigeToast('Verschieben fehlgeschlagen: ' + fehler, true);
    else if (anzahl > 0) zeigeToast(`${anzahl === 1 ? 'E-Mail' : `${anzahl} E-Mails`} nach „${ordnerLabel(ziel)}“ verschoben`);
  };

  const verschiebeRef = useRef(verschiebeInOrdner);
  verschiebeRef.current = verschiebeInOrdner;

  const ordneEmailObjektZu = async (emailId: string, objektId: string) => {
    const { error } = await supabase.from('emails')
      .update({ objekt_id: objektId, zuordnung_typ: 'manuell' }).eq('id', emailId);
    if (error) { console.error('Objekt-Zuordnung fehlgeschlagen:', error); return; }
    fetchEmails(true);
  };

  // ── Mehrfachauswahl + Sammelaktionen ──────────────────────────────────────
  const alleAuswaehlen = () => {
    setSelectedEmailIds(prev => {
      const sichtbare = filteredEmails.map(e => e.id);
      const alleGewaehlt = sichtbare.length > 0 && sichtbare.every(id => prev.has(id));
      return alleGewaehlt ? new Set() : new Set(sichtbare);
    });
  };
  const clearAuswahl = () => setSelectedEmailIds(new Set());

  const bulkErledigt = async (wert: boolean) => {
    const ids = Array.from(selectedEmailIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      // Als erledigt markiert hebt eine offene Wiedervorlage automatisch auf (Spec, wie toggleErledigt).
      const update: { erledigt: boolean; wiedervorlage_am?: null } = { erledigt: wert };
      if (wert) update.wiedervorlage_am = null;
      const { error } = await supabase.from('emails').update(update).in('id', ids);
      if (error) throw error;
      clearAuswahl();
      fetchOffenCount();
      await fetchEmails(true);
    } catch (err: any) {
      zeigeToast('Aktion fehlgeschlagen: ' + (err?.message || 'Unbekannt'), true);
    } finally {
      setBulkBusy(false);
    }
  };

  // Sammel-Löschen: endgültig (wie das Löschen einer einzelnen Mail) — deshalb IMMER mit
  // Rückfrage samt Anzahl. Gewünscht vor allem für Spam (User 2026-08-11).
  const bulkWiederherstellen = async () => {
    const ids = Array.from(selectedEmailIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    const { error } = await supabase.from('emails').update({ geloescht_am: null, geloescht_von: null }).in('id', ids);
    setBulkBusy(false);
    if (error) { zeigeToast('Wiederherstellen fehlgeschlagen: ' + error.message, true); return; }
    zeigeToast(`${ids.length === 1 ? 'E-Mail' : `${ids.length} E-Mails`} wiederhergestellt`);
    clearAuswahl();
    fetchOffenCount();
    await fetchEmails(true);
  };
  const bulkLoeschen = () => {
    const ids = Array.from(selectedEmailIds);
    if (ids.length === 0) return;
    setLoeschFrage({ ids });
  };
  const bulkLoeschenAusfuehren = async (ids: string[]) => {
    setBulkBusy(true);
    try {
      const { error } = await loescheEmails(ids, activeTab === 'geloescht');
      if (error) throw error;
      zeigeToast(activeTab === 'geloescht' ? `${ids.length === 1 ? 'E-Mail' : `${ids.length} E-Mails`} endgültig gelöscht` : `${ids.length === 1 ? 'E-Mail' : `${ids.length} E-Mails`} in „Gelöscht“ verschoben`);
      if (selectedEmail && ids.includes(selectedEmail.id)) setSelectedEmail(null);
      clearAuswahl();
      fetchOffenCount();
      await fetchEmails(true);
    } catch (err: any) {
      zeigeToast('Löschen fehlgeschlagen: ' + (err?.message || 'Unbekannt'), true);
    } finally {
      setBulkBusy(false);
      setLoeschFrage(null);
    }
  };

  const bulkArchivieren = async (archivieren: boolean) => {
    const ids = Array.from(selectedEmailIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      // Archivieren hebt eine offene Wiedervorlage automatisch auf (Spec).
      const update: { archiviert_am: string | null; wiedervorlage_am?: null } = { archiviert_am: archivieren ? new Date().toISOString() : null };
      if (archivieren) update.wiedervorlage_am = null;
      const { error } = await supabase.from('emails')
        .update(update).in('id', ids);
      if (error) throw error;
      clearAuswahl();
      await fetchEmails(true);
    } catch (err: any) {
      zeigeToast('Aktion fehlgeschlagen: ' + (err?.message || 'Unbekannt'), true);
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkGelesen = async () => {
    const ids = Array.from(selectedEmailIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { error } = await supabase.from('emails').update({ gelesen: true }).in('id', ids);
      if (error) throw error;
      clearAuswahl();
      await fetchEmails(true);
    } catch (err: any) {
      zeigeToast('Aktion fehlgeschlagen: ' + (err?.message || 'Unbekannt'), true);
    } finally {
      setBulkBusy(false);
    }
  };

  // Werbung-Flag im Sammel: markieren (→ Werbung-Ordner) bzw. im Werbung-Tab zurückholen
  const bulkWerbung = async (istWerbung: boolean) => {
    const ids = Array.from(selectedEmailIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { error } = await supabase.from('emails').update({ ist_werbung: istWerbung }).in('id', ids);
      if (error) throw error;
      clearAuswahl();
      fetchOffenCount();
      await fetchEmails(true);
    } catch (err: any) {
      zeigeToast('Aktion fehlgeschlagen: ' + (err?.message || 'Unbekannt'), true);
    } finally {
      setBulkBusy(false);
    }
  };

  // Einzel-Archivierung (Kontextmenü)
  const archiviereEinzeln = async (emailId: string, archivieren: boolean) => {
    try {
      // Archivieren hebt eine offene Wiedervorlage automatisch auf (Spec).
      const update: { archiviert_am: string | null; wiedervorlage_am?: null } = { archiviert_am: archivieren ? new Date().toISOString() : null };
      if (archivieren) update.wiedervorlage_am = null;
      const { error } = await supabase.from('emails')
        .update(update).eq('id', emailId);
      if (error) throw error;
      if (selectedEmail?.id === emailId) setSelectedEmail(null);
      await fetchEmails(true);
    } catch (err: any) {
      zeigeToast('Archivieren fehlgeschlagen: ' + (err?.message || 'Unbekannt'), true);
    }
  };

  // Wiedervorlage setzen/aufheben — null = aufheben. Benachrichtigt-Merker zurücksetzen,
  // damit der Weckruf-Cron beim neuen Termin wieder anschlägt.
  const setzeWiedervorlage = async (emailId: string, datumIso: string | null) => {
    try {
      await supabase.from('emails')
        .update({ wiedervorlage_am: datumIso, wiedervorlage_benachrichtigt: false })
        .eq('id', emailId);
      await fetchEmails(true);
    } catch (e) {
      zeigeToast(`Wiedervorlage fehlgeschlagen: ${(e as Error).message}`, true);
    }
  };

  const bulkVerschieben = async (objektId: string) => {
    const ids = Array.from(selectedEmailIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { error } = await supabase.from('emails').update({ objekt_id: objektId, zuordnung_typ: 'manuell' }).in('id', ids);
      if (error) throw error;
      clearAuswahl();
      await fetchEmails(true);
    } catch (err: any) {
      zeigeToast('Verschieben fehlgeschlagen: ' + (err?.message || 'Unbekannt'), true);
    } finally {
      setBulkBusy(false);
    }
  };

  // ── E-Mail-Nav in die Sidebar spiegeln (Postfach + Ordner + Neue E-Mail) ──
  const { setNav } = useEmailNav();
  const { openForm, closeForm } = useIslandActions();
  const handleCreateKontakt = (initial: { vorname: string; nachname: string; firmenname?: string; email: string; festnetz?: string; handynummer?: string }) => {
    openForm(
      <KontaktErstellenFormular initial={initial} onDone={() => closeForm()} />,
      'Neuer Kontakt',
      640,
      { icon: PhAddressBook, farbe: 'violett', badge: 'plus', pflichtHinweis: true },
    );
  };
  // #570: Termin aus einer Mail – Formular mit Vorbelegung (Datum/Uhrzeit aus dem Text, Zuordnungen der Mail).
  const oeffneTerminAusMail = (v: TerminAusMail) => {
    openForm(
      <TerminFormular
        variant="pille"
        editTermin={null}
        prefillDate={v.start}
        prefillEnde={v.ende}
        teamMembers={teamMembers}
        userProfile={userProfile}
        prefillTitel={v.titel}
        prefillBeschreibung={v.beschreibung}
        prefillObjektId={v.objektId}
        prefillMieterId={v.mieterId}
        prefillEigentuemerId={v.eigentuemerId}
        onClose={() => closeForm()}
        onSaved={() => { closeForm(); zeigeToast('Termin angelegt'); }}
      />,
      'Termin aus E-Mail',
      520,
      { icon: PhCalendarPlus, farbe: 'rot', badge: 'plus', pflichtHinweis: true },
    );
  };
  // Aus dem Rechtsklick-Menü: Die Liste lädt den HTML-Inhalt nicht mit – bei Bedarf nachladen.
  const terminAusListe = async (em: Email) => {
    let text = em.text_inhalt || '';
    if (!text.trim()) {
      const { data } = await supabase.from('emails').select('html_inhalt').eq('id', em.id).maybeSingle();
      text = mailKlartext((data as { html_inhalt?: string | null } | null)?.html_inhalt ?? '');
    }
    oeffneTerminAusMail(terminAusMail(em, text, new Date()));
  };
  useEffect(() => {
    const konto = konten.find(k => k.id === selectedKontoId);
    setNav({
      konten: konten.map((k) => ({ id: k.id, anzeigename: k.anzeigename, email_adresse: k.email_adresse, bezeichnung: k.bezeichnung ?? null, letzter_sync: k.letzter_sync ?? null })),
      onRefresh: () => { fetchEmails(); fetchKonten(); },
      selectedKontoId,
      onSelectKonto: (id) => { setSelectedKontoId(id); setSelectedEmail(null); setSelectedEmailIds(new Set()); },
      activeTab,
      onSelectTab: (t: EmailOrdner) => { setActiveTab(t); setSelectedObjektId(null); setSelectedEmail(null); setSelectedEmailIds(new Set()); },
      unreadCount,
      offenCount,
      wiedervorlageCount,
      rueckmeldungCount,
      werbungCount,
      archiviertCount,
      entwuerfeCount,
      geplantCount,
      objekte: objekte.map((o) => ({ ...o, unread: objektUnread[o.id] || 0 })),
      selectedObjektId,
      onSelectObjekt: (id: string) => { setSelectedObjektId(id); setActiveTab('objekt'); setSelectedEmail(null); setSelectedEmailIds(new Set()); },
      onAssignObjekt: ordneEmailObjektZu,
      // über die Ref: der Nav-Effekt läuft nicht bei jeder Listenänderung neu, die Liste wäre sonst veraltet
      onDropInOrdner: (ids, ziel) => { void verschiebeRef.current(ids, ziel); },
      // Neue E-Mail öffnet das Schreibfenster (E4).
      onCompose: () => {
        if (!selectedKontoId || !konto) return;
        // Nach dem Schließen kurz warten, bis das Auto-Speichern (Unmount) durch ist, dann Entwürfe aktualisieren.
        oeffneVerfassen({ nachher: () => { setTimeout(() => { fetchEntwuerfeCount(); if (activeTab === 'entwuerfe') fetchEmails(true); }, 1200); } });
      },
    });
    return () => setNav(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [konten, selectedKontoId, activeTab, unreadCount, offenCount, wiedervorlageCount, rueckmeldungCount, werbungCount, archiviertCount, entwuerfeCount, geplantCount, objekte, objektUnread, selectedObjektId]);

  // #551: das Mailfenster meldet jede Änderung ans Postfach. Muss vor dem frühen return stehen (Hook-Reihenfolge).
  useEffect(() => { if (fenster && fetchAbgeschlossen) meldeAenderung(); }, [emails]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Loading state ────────────────────────────────────────────────────────

  if (kontenLoading) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ padding: '96px 0', gap: 12 }}>
        <CircleNotch size={36} className="animate-spin" color="var(--ink3)" />
        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>E-Mail-Konten werden geladen...</p>
      </div>
    );
  }

  // ── No accounts configured ──────────────────────────────────────────────

  const keineKonten = konten.length === 0;
  const listenOrdner = activeTab === 'posteingang' || activeTab === 'offen' || activeTab === 'erledigt' || activeTab === 'wiedervorlage' || activeTab === 'rueckmeldung' || activeTab === 'gesendet' || activeTab === 'geplant' || activeTab === 'entwuerfe' || activeTab === 'werbung' || activeTab === 'archiviert' || activeTab === 'geloescht' || activeTab === 'objekt';
  const leerText = hasActiveFilters ? 'Keine E-Mails gefunden' : activeTab === 'werbung' ? 'Keine Werbung' : activeTab === 'archiviert' ? 'Archiv ist leer' : activeTab === 'geloescht' ? 'Keine gelöschten E-Mails' : activeTab === 'entwuerfe' ? 'Keine Entwürfe' : activeTab === 'geplant' ? 'Keine geplanten E-Mails' : activeTab === 'objekt' ? 'Keine E-Mails für dieses Objekt' : activeTab === 'wiedervorlage' ? 'Keine Wiedervorlagen geplant' : activeTab === 'rueckmeldung' ? 'Keine Mail wartet auf Rückmeldung – per Rechtsklick „Rückmeldung?“ markieren; kommt eine Antwort im selben Verlauf, fällt die Markierung von selbst weg' : activeTab === 'offen' ? 'Keine offenen E-Mails' : activeTab === 'erledigt' ? 'Nichts als erledigt markiert' : activeTab === 'posteingang' ? 'Posteingang ist leer' : 'Keine gesendeten E-Mails';

  const filterProps: FilterPanelProps = {
    werte: { gelesen: filterGelesen, zuordnung: filterZuordnung, erledigt: filterErledigt, zustaendig: filterZustaendig, markiert: filterMarkiert, anhang: filterAnhang, triage: filterTriage },
    setGelesen: setFilterGelesen, setZuordnung: setFilterZuordnung, setErledigt: setFilterErledigt, setZustaendig: setFilterZustaendig,
    setMarkiert: setFilterMarkiert, setAnhang: setFilterAnhang, setTriage: setFilterTriage,
    teams, teamMembers, mitTriage: activeTab === 'posteingang', ohneTicketAnzahl: triageOhneTicketCount, notfallAnzahl: triageNotfallCount,
    anzahl: filteredEmails.length, hatFilter: hasActiveFilters, onReset: resetFilters,
  };

  // Pin-Sortierung (fetchEmails) zieht fällige Wiedervorlagen in posteingang/offen/erledigt nach oben — diese Tabs
  // bekommen EINE eigene „Wiedervorlage fällig“-Gruppe an der Spitze statt eines doppelten Datums-Kopfs.
  const jetzt = new Date();
  const pinGruppeAktiv = activeTab === 'posteingang' || activeTab === 'offen' || activeTab === 'erledigt';
  // Konversations-Gruppierung — neueste Mail je conversation_id wird Hauptzeile (Entwürfe bleiben ungruppiert).
  const anzeigeGruppen = gruppiereNachKonversation(filteredEmails, gruppiereKonversationen && activeTab !== 'entwuerfe');
  const absenderKuerzel: Record<string, string> = { mieter: 'M', eigentuemer: 'E', dienstleister: 'DL', behoerde: 'B' };
  const oeffneMenue = (e: React.MouseEvent, email: Email) => { e.preventDefault(); setEmailMenu({ x: e.clientX, y: e.clientY, email }); };
  const kiVon = (email: Email): KiKennzeichen | null => {
    const a = analysen[email.id];
    const hatTicket = ticketsByEmail.has(email.id);
    if (!a && !hatTicket) return null;
    const kat = a?.kategorie_id ? ticketKategorien.find((k: { id: string }) => k.id === a.kategorie_id) as { name: string; farbe?: string | null } | undefined : undefined;
    return {
      kategorie: kat ? { name: kat.name, farbe: kat.farbe ?? null } : null,
      notfall: !!a?.ist_notfall,
      dringend: a?.prioritaet === 'dringend',
      kuerzel: a?.absender_typ ? absenderKuerzel[a.absender_typ] ?? null : null,
      absenderTyp: a?.absender_typ ?? null,
      prozent: a?.confidence != null ? Math.round(a.confidence * 100) : null,
      ticket: hatTicket,
    };
  };

  // #551: Postfach lädt auf Meldung aus einem Mailfenster still nach; das Mailfenster meldet jede Änderung.
  nachladenRef.current = () => { void fetchEmails(true); };

  // #551: Lesefenster schließen — im eigenen Mailfenster schließt das ganze Fenster.
  const schliesseLesen = () => {
    if (!fenster) { setSelectedEmail(null); return; }
    window.close();
    // #572: Falls der Browser das Schließen verweigert, schließt das Postfach (es hat das Fenster geöffnet).
    setTimeout(() => { if (!window.closed) kanalRef.current?.postMessage({ typ: 'schliessen' }); }, 300);
  };

  const menueAktionen = (em: Email): MenueAktion[] => {
    const zu = (fn: () => void) => () => { setEmailMenu(null); fn(); };
    const l: MenueAktion[] = [
      { icon: EnvelopeOpen, label: 'Öffnen', onClick: zu(() => handleSelectEmail(em)) },
      { icon: em.gelesen ? EnvelopeSimple : EnvelopeOpen, label: em.gelesen ? 'Als ungelesen' : 'Als gelesen', onClick: zu(() => toggleGelesen(em)) },
      { icon: PhStar, label: em.markiert ? 'Stern entfernen' : 'Markieren', onClick: zu(() => toggleStern(em)), aktivFarbe: em.markiert ? 'var(--warn)' : undefined },
    ];
    if (em.ordner === 'SCHEDULED' || em.ordner === 'SENDING') {
      l.push({ icon: PaperPlaneTilt, label: 'Jetzt senden', onClick: zu(() => jetztSenden(em)) });
      l.push({ icon: PhX, label: 'Abbrechen', onClick: zu(() => geplantenVersandAbbrechen(em)) });
    }
    // #527: Zuweisen und „In Bearbeitung“
    l.push({ icon: PhUserPlus, label: em.zustaendig_id ? 'Zuweisung ändern…' : 'Zuweisen…', onClick: () => { if (!emailMenu) return; const { x, y } = emailMenu; setEmailMenu(null); setZuweisenPicker({ email: em, x, y }); } });
    const b = aktiveBearbeitung(em, new Date());
    if (!b) l.push({ icon: PhInBearbeitung, label: 'In Bearbeitung nehmen', onClick: zu(() => { void bearbeitungNehmen(em); }) });
    else if (b.vonId === meineId) l.push({ icon: PhInBearbeitung, label: 'Bearbeitung beenden', onClick: zu(() => { void bearbeitungBeenden(em); }), aktivFarbe: 'var(--warn)' });
    else {
      const wer = kurzname(teamMembers.find(m => m.id === b.vonId)?.full_name);
      l.push({ icon: PhInBearbeitung, label: `Von ${wer} übernehmen`, aktivFarbe: 'var(--warn)', onClick: zu(() => { if (window.confirm(`${wer} bearbeitet diese Mail gerade. Trotzdem übernehmen?`)) void bearbeitungNehmen(em, true); }) });
    }
    // #552: Wiedervorlage auch für gesendete Mails (z. B. „Antwort bis Freitag?“) — ohne Ticket.
    if (em.richtung === 'eingang' || em.ordner === 'SENT') {
      l.push({ icon: PhClock, label: 'Wiedervorlage…', onClick: () => { if (!emailMenu) return; const { x, y } = emailMenu; setEmailMenu(null); setWiedervorlagePicker({ emailId: em.id, x, y }); } });
      if (em.wiedervorlage_am) l.push({ icon: PhX, label: 'Wiedervorlage aufheben', onClick: zu(() => { void setzeWiedervorlage(em.id, null); }) });
    }
    if (em.richtung === 'eingang') {
      l.push({ icon: PhCheckCircle, label: em.erledigt ? 'Als offen markieren' : 'Als erledigt markieren', onClick: zu(() => toggleErledigt(em)), aktivFarbe: em.erledigt ? 'var(--ok)' : undefined });
      // #551: auf Antwort warten (Ordner „Rückmeldung“; Antwort im Verlauf räumt es automatisch ab)
      l.push(em.rueckmeldung_seit
        ? { icon: PhHourglass, label: 'Rückmeldung erhalten', aktivFarbe: '#8a63d2', onClick: zu(() => { void setzeRueckmeldung(em, false); }) }
        : { icon: PhHourglass, label: 'Rückmeldung?', onClick: zu(() => { void setzeRueckmeldung(em, true); }) });
    }
    l.push({ icon: PhCalendarPlus, label: 'Termin anlegen…', onClick: zu(() => { void terminAusListe(em); }) });
    l.push({
      icon: PhMegaphone, label: em.ist_werbung ? 'Keine Werbung' : 'Als Werbung',
      onClick: zu(() => {
        (async () => {
          try {
            await setzeWerbungManuell(em.id, !em.ist_werbung);
            setSelectedEmail(null);
            fetchEmails(true);
          } catch (e) {
            console.error('Werbung-Markierung fehlgeschlagen:', e);
          }
        })();
      }),
    });
    if (em.richtung === 'eingang') l.push({ icon: PhArchive, label: em.archiviert_am ? 'Aus Archiv holen' : 'Archivieren', onClick: zu(() => archiviereEinzeln(em.id, !em.archiviert_am)) });
    return l;
  };

  // ── Main render ──────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      {listenOrdner && keineKonten && (
        <LeerZustand icon={WarningCircle} titel="Kein Postfach angebunden" text="Das Postfach wird zentral über Microsoft 365 (Graph) angebunden und automatisch synchronisiert." />
      )}

      {listenOrdner && !keineKonten && (
        <div>
          <div className="flex flex-col min-w-0 w-full" style={{ gap: 10 }}>
            <ListenKopf
              suche={searchTerm}
              onSuche={setSearchTerm}
              onSucheLeeren={() => { setSearchTerm(''); setDebouncedSearch(''); }}
              filter={filterProps}
              alleGewaehlt={filteredEmails.length > 0 && filteredEmails.every(e => selectedEmailIds.has(e.id))}
              onAlleAuswaehlen={alleAuswaehlen}
              konversationen={gruppiereKonversationen}
              onKonversationen={toggleKonversationen}
              zeigeAuswahl={!loading && filteredEmails.length > 0}
              mobil={mobil}
            />

            {selectedEmailIds.size > 0 && (
              <Sammelleiste
                anzahl={selectedEmailIds.size}
                busy={bulkBusy}
                werbungOrdner={activeTab === 'werbung'}
                archivOrdner={activeTab === 'archiviert'}
                geloeschtOrdner={activeTab === 'geloescht'}
                onWiederherstellen={() => { void bulkWiederherstellen(); }}
                objekte={objekte}
                mobil={mobil}
                angeheftet={!quer}
                onErledigt={(w) => { void bulkErledigt(w); }}
                onGelesen={() => { void bulkGelesen(); }}
                onWerbung={(w) => { void bulkWerbung(w); }}
                onArchivieren={(w) => { void bulkArchivieren(w); }}
                onLoeschen={bulkLoeschen}
                onVerschieben={(id) => { void bulkVerschieben(id); }}
                onAufheben={clearAuswahl}
              />
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center" style={{ padding: '72px 0', gap: 12 }}>
                <CircleNotch size={30} className="animate-spin" color="var(--ink3)" />
                <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>E-Mails werden geladen...</p>
              </div>
            ) : filteredEmails.length === 0 ? (
              <LeerZustand icon={Tray} titel={leerText}>
                {hasActiveFilters && (
                  <button type="button" onClick={resetFilters} className="flex items-center cursor-pointer"
                    style={{ marginTop: 6, height: 44, padding: '0 16px', gap: 6, borderRadius: 22, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 13, fontWeight: 700 }}>
                    <PhX size={13} weight="bold" />Filter zurücksetzen
                  </button>
                )}
              </LeerZustand>
            ) : (
              <div className="flex flex-col" style={{ gap: 8 }}>
                {(() => {
                  let lastGroup: string | null = null;
                  return anzeigeGruppen.map(({ email, rest }) => {
                    const convKey = email.conversation_id || `solo-${email.id}`;
                    const gruppeIds = [email.id, ...rest.map(m => m.id)];
                    const gruppeGewaehlt = gruppeIds.every(id => selectedEmailIds.has(id));
                    const badges = [getBearbeitungBadge(email), getErledigtBadge(email), getWiedervorlageBadge(email), getBeantwortetBadge(email), getRueckmeldungBadge(email), getGeplantBadge(email), getZustaendigBadge(email), getRechnungBadge(email), getAblageBadge(email)]
                      .filter((b): b is ZeilenBadge => !!b);
                    const anzeigeName = activeTab === 'objekt'
                      ? (email.richtung === 'ausgang' ? (empfaengerAlsText(email.an) || email.von) : (kontaktName(email) || email.von))
                      : (activeTab === 'posteingang' || activeTab === 'werbung' || activeTab === 'offen' || activeTab === 'erledigt' || activeTab === 'archiviert' || activeTab === 'geloescht')
                        ? (email.richtung === 'ausgang' ? `An: ${empfaengerAlsText(email.an) || '–'}` : (kontaktName(email) || email.von))
                        : (empfaengerAlsText(email.an) || (activeTab === 'entwuerfe' ? '(Kein Empfänger)' : email.von));
                    const gruppe = (pinGruppeAktiv && istWiedervorlageFaellig(email.wiedervorlage_am, jetzt))
                      ? 'Wiedervorlage fällig'
                      : datumsGruppe(email.datum, jetzt);
                    const zeigeKopf = gruppe !== lastGroup;
                    lastGroup = gruppe;
                    const offen = expandedConvs.has(convKey);

                    return (
                      <React.Fragment key={email.id}>
                        {zeigeKopf && (
                          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink3)', padding: '10px 6px 2px' }}>{gruppe}</span>
                        )}
                        <EmailZeile
                          id={email.id}
                          name={anzeigeName}
                          initialen={initialen(anzeigeName)}
                          betreff={email.betreff || '(Kein Betreff)'}
                          vorschau={kuerze(email.text_inhalt, 80)}
                          datum={relativesDatum(email.datum, jetzt)}
                          ausgang={email.richtung === 'ausgang'}
                          gruppeUngelesen={!email.gelesen}
                          ungelesen={!email.gelesen}
                          hatAnhang={!!email.hat_anhaenge}
                          aktiv={selectedEmail?.id === email.id}
                          gewaehlt={gruppeGewaehlt}
                          auswahlTitel={gruppeGewaehlt ? 'Abwählen' : rest.length ? 'Konversation auswählen' : 'Auswählen'}
                          ziehend={draggingId === email.id}
                          badges={badges}
                          chips={getZuordnungBadge(email)}
                          ki={kiVon(email)}
                          erledigt={email.richtung === 'eingang' ? !!email.erledigt : null}
                          markiert={email.markiert}
                          farbe={(() => { const eff = effektiveZustaendigkeit(email); return eff ? farben.get(eff.id) ?? null : null; })()}
                          mobil={mobil}
                          konversation={rest.length > 0 ? {
                            anzahl: rest.length + 1,
                            offen,
                            onUmschalten: () => setExpandedConvs(prev => {
                              const n = new Set(prev);
                              if (n.has(convKey)) n.delete(convKey); else n.add(convKey);
                              return n;
                            }),
                            zeilen: rest.map(m => ({
                              id: m.id,
                              name: m.richtung === 'ausgang' ? (empfaengerAlsText(m.an) || 'Gesendet') : (kontaktName(m) || m.von),
                              ausgang: m.richtung === 'ausgang',
                              ungelesen: !m.gelesen,
                              text: kuerze(m.text_inhalt, 60),
                              anhang: !!m.hat_anhaenge,
                              datum: relativesDatum(m.datum, jetzt),
                              aktiv: selectedEmail?.id === m.id,
                              onClick: () => handleSelectEmail(m),
                              onKontext: (e: React.MouseEvent) => oeffneMenue(e, m),
                            })),
                          } : null}
                          onOeffnen={() => { if (activeTab === 'entwuerfe') oeffneEntwurf(email); else handleSelectEmail(email); }}
                          onKontext={(e) => oeffneMenue(e, email)}
                          onAuswahl={() => {
                            // Gruppierte Konversation: Auswahl gilt für alle Mails der Gruppe
                            setSelectedEmailIds(prev => {
                              const n = new Set(prev);
                              gruppeIds.forEach(id => { if (gruppeGewaehlt) n.delete(id); else n.add(id); });
                              return n;
                            });
                          }}
                          onErledigt={(e) => { void toggleErledigt(email, e); }}
                          onStern={(e) => { void toggleMarkiert(e, email); }}
                          onDragStart={(e) => {
                            e.dataTransfer.setData('application/email-id', email.id);
                            // #578: Ist die gezogene Mail Teil einer Mehrfachauswahl, wandern alle markierten mit (Ordner-Ablage).
                            if (selectedEmailIds.has(email.id) && selectedEmailIds.size > 1) {
                              e.dataTransfer.setData('application/email-ids', JSON.stringify(Array.from(selectedEmailIds)));
                            }
                            e.dataTransfer.effectAllowed = 'move';
                            // Kleines Drag-Bild (Pille), damit die Objekt-Ordner sichtbar bleiben.
                            const pill = document.createElement('div');
                            pill.textContent = email.betreff || anzeigeName || 'E-Mail';
                            pill.style.cssText = 'position:fixed;top:-1000px;left:-1000px;max-width:240px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding:6px 12px;border-radius:9999px;background:var(--ink);color:var(--bg);font-family:Manrope,sans-serif;font-size:12px;font-weight:700;box-shadow:0 10px 20px rgba(0,0,0,.25)';
                            document.body.appendChild(pill);
                            e.dataTransfer.setDragImage(pill, 16, 16);
                            setTimeout(() => pill.remove(), 0);
                            setDraggingId(email.id);
                          }}
                          onDragEnd={() => setDraggingId(null)}
                        />
                      </React.Fragment>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vorlagen Tab */}
      {activeTab === 'vorlagen' && (
        <EmailVorlagenGrid userProfile={userProfile} />
      )}

      {/* Serienmail (Ordner im neuen Design) */}
      {activeTab === 'serienmail' && serienmail}

      {/* Einstellungen Tab */}
      {activeTab === 'einstellungen' && (
        <EmailKontoEinstellungen
          userProfile={userProfile}
          onOpenFormular={(konto) => setKontoZumBearbeiten(konto)}
        />
      )}

      {/* Lesefenster (GlassModal, Handoff §6): Esc/Klick daneben schließt, ↑/↓ blättern, Vollbild.
          Antworten/Weiterleiten öffnet das Schreibfenster darüber. */}
      {selectedEmail && listenOrdner && !keineKonten && (
        <GlassModal
          label={selectedEmail.betreff || 'E-Mail'}
          vollbild={lesenVollbild}
          mobil={mobil}
          onVollbildAus={fenster ? undefined : () => setLesenVollbild(false)}
          onSchliessen={schliesseLesen}
        >
            <EmailDetailsSidebar
              email={selectedEmail}
              mobil={mobil}
              kopf={{
                onVorherige: lesenIdx > 0 ? () => geheZuMail(lesenIdx - 1) : undefined,
                onNaechste: lesenIdx >= 0 && lesenIdx < filteredEmails.length - 1 ? () => geheZuMail(lesenIdx + 1) : undefined,
                vollbild: lesenVollbild,
                onVollbild: () => setLesenVollbild(v => !v),
              }}
              onClose={schliesseLesen}
              onReply={() => { if (selectedEmail) void oeffneAntwortOderWeiterleitung(selectedEmail, 'reply'); }}
              onReplyAll={() => { if (selectedEmail) void oeffneAntwortOderWeiterleitung(selectedEmail, 'replyAll'); }}
              eigeneAdresse={selectedKonto?.email_adresse}
              onForward={() => { if (selectedEmail) void oeffneAntwortOderWeiterleitung(selectedEmail, 'forward'); }}
              onDeleted={() => { if (fenster) { meldeAenderung(); window.close(); return; } setSelectedEmail(null); fetchEmails(true); }}
              onUpdated={() => fetchEmails(true)}
              onWiedervorlage={(iso) => { if (selectedEmail) void setzeWiedervorlage(selectedEmail.id, iso); }}
              onToggleErledigt={() => { if (selectedEmail) void toggleErledigt(selectedEmail); }}
              ticketKategorien={ticketKategorien}
              userProfile={userProfile}
              ticketRefresh={ticketRefresh}
              onChanged={() => setTicketRefresh((n) => n + 1)}
              onCreateTicket={async (data) => {
                const [katRes, teamRes] = await Promise.all([
                  supabase.from('ticket_kategorien').select('*').eq('aktiv', true).order('sortierung'),
                  supabase.from('profiles').select('id, full_name').in('role', ['admin', 'team', 'super admin']).eq('zuweisbar', true),
                ]);
                setTicketKategorien(katRes.data || []);
                setTicketTeamMembers(teamRes.data || []);
                setTicketInitialData(data);
              }}
              onCreateKontakt={handleCreateKontakt}
              onCreateTermin={oeffneTerminAusMail}
            />
        </GlassModal>
      )}

      {/* Rechtsklick-Kontextmenü auf einer E-Mail */}
      {emailMenu && (
        <EmailKontextmenue
          x={emailMenu.x}
          y={emailMenu.y}
          aktionen={menueAktionen(emailMenu.email)}
          onLoeschen={() => { const em = emailMenu.email; setEmailMenu(null); setLoeschFrage({ email: em }); }}
        />
      )}

      {/* Wiedervorlage-Wahl: an den Klick-Koordinaten des Kontextmenüs positioniert */}
      {zuweisenPicker && (
        <ZuweisenWahl x={zuweisenPicker.x} y={zuweisenPicker.y} teams={teams} mitarbeiter={teamMembers} farben={farben}
          aktuell={zuweisenPicker.email.zustaendig_id ?? null}
          onWahl={(wahl) => { const em = zuweisenPicker.email; setZuweisenPicker(null); void setzeZustaendig(em, wahl); }}
          onSchliessen={() => setZuweisenPicker(null)} />
      )}
      {wiedervorlagePicker && (
        <WiedervorlageWahl
          x={wiedervorlagePicker.x}
          y={wiedervorlagePicker.y}
          onWahl={(iso) => { void setzeWiedervorlage(wiedervorlagePicker.emailId, iso); setWiedervorlagePicker(null); }}
          onAufheben={() => { void setzeWiedervorlage(wiedervorlagePicker.emailId, null); setWiedervorlagePicker(null); }}
          onSchliessen={() => setWiedervorlagePicker(null)}
        />
      )}

      {loeschFrage && (() => {
        const anzahl = 'ids' in loeschFrage ? loeschFrage.ids.length : 1;
        return (
          <BestaetigenDialog
            titel={activeTab === 'geloescht'
              ? (anzahl === 1 ? 'Diese E-Mail endgültig löschen?' : `${anzahl} E-Mails endgültig löschen?`)
              : (anzahl === 1 ? 'Diese E-Mail löschen?' : `${anzahl} E-Mails löschen?`)}
            text={activeTab === 'geloescht'
              ? 'Das lässt sich nicht rückgängig machen. In Outlook bleibt die Mail erhalten.'
              : 'Sie werden in den Ordner „Gelöscht“ verschoben und lassen sich dort wiederherstellen. Der Ordner leert sich nicht von selbst.'}
            bestaetigenLabel="Löschen"
            laeuft={bulkBusy}
            onAbbrechen={() => setLoeschFrage(null)}
            onBestaetigen={() => {
              if ('ids' in loeschFrage) { void bulkLoeschenAusfuehren(loeschFrage.ids); return; }
              const em = loeschFrage.email;
              setLoeschFrage(null);
              void deleteEmail(em);
            }}
          />
        );
      })()}

      <SchwebenderToast toast={toast} mobil={mobil} />

      {/* Schreibfenster (E4): ein Fenster für Antworten, Weiterleiten, Neue E-Mail und Entwürfe */}
      {verfassen && selectedKonto && (
        <EmailVerfassen
          key={verfassen.nr}
          variant="fenster"
          titel={verfassen.titel}
          konto={{ id: selectedKontoId, email_adresse: selectedKonto?.email_adresse || '', bezeichnung: selectedKonto?.anzeigename || selectedKonto?.email_adresse || '' }}
          replyTo={verfassen.replyTo}
          forward={verfassen.forward}
          draft={verfassen.draft}
          onClose={() => { const n = verfassen.nachher; setVerfassen(null); n?.(); }}
          onSent={() => {
            const n = verfassen.nachher;
            const ursprung = verfassen.antwortAuf;
            setVerfassen(null);
            fetchEmails(true);
            // #553: Antwort raus — Ursprungsmail gleich als erledigt markieren (ein Klick), damit niemand doppelt antwortet.
            if (ursprung && !ursprung.erledigt) {
              zeigeToast('Antwort gesendet', false, { label: 'Als erledigt markieren', onClick: () => { void toggleErledigt({ ...ursprung, erledigt: false }); } });
            }
            n?.();
          }}
        />
      )}

      {/* Konto Formular Modal */}
      {kontoZumBearbeiten && (
        <EmailKontoFormular
          konto={kontoZumBearbeiten}
          onClose={() => setKontoZumBearbeiten(null)}
          onSaved={() => {
            setKontoZumBearbeiten(null);
            fetchKonten();
          }}
        />
      )}

      {/* Ticket aus E-Mail erstellen */}
      {ticketInitialData && (
        <TicketFormular
          kategorien={ticketKategorien}
          teamMembers={ticketTeamMembers}
          userProfile={userProfile}
          initialData={ticketInitialData}
          onClose={() => setTicketInitialData(null)}
          onCreated={async (ticketId) => {
            if (ticketId && selectedEmail?.id) {
              try {
                await verknuepfeTicketMitEmail(ticketId, selectedEmail.id);
                const analysenId = analysen[selectedEmail.id]?.id;
                if (analysenId) await setzeCockpitStatus(analysenId, 'freigegeben');
              } catch (e) { console.warn('Ticket-Verknuepfung fehlgeschlagen', e); }
            }
            setTicketInitialData(null);
            setTicketRefresh((n) => n + 1);
          }}
        />
      )}
    </div>
  );
};

export default EmailListe;
